import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  Chip,
  Divider,
  Collapse,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import { UploadFile, Insights, ExpandMore, ChevronRight, AccountTree } from '@mui/icons-material';

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const CLASS_TREE_ROOTS = [
  { id: 'nen2660:RealObject', defaultLabel: 'RealObject' },
  { id: 'nen2660:SpatialRegion', defaultLabel: 'SpatialRegion' }
];

const CLASS_CODE_PROPERTY_ID = 'tennet:8d464b9745fc848aca8ae713a8dcf536';
const GENERAL_FAST_LIMITS = {
  roots: 12,
  classes: 120,
  instances: 140,
  plans: 140,
  requirements: 160
};
const MAX_NODE_CONNECTIONS_IN_PANEL = 80;
const EDGE_TYPE_LABELS = {
  'root-class': 'Raiz -> Clase',
  'class-instance': 'Clase -> Objeto/Region',
  'object-class': 'Objeto -> Clase',
  'instance-plan': 'Objeto/Clase -> VerificationPlan',
  'class-plan': 'Clase -> VerificationPlan',
  'plan-requirement': 'VerificationPlan -> Requirement',
  'class-requirement': 'Clase -> Requirement'
};
const MAX_ONTOLOGY_MATCH_RESULTS = 320;
const createOntologyMatchState = () => ({
  hasCompared: false,
  sourceUniqueIds: 0,
  ontologyUniqueIds: 0,
  sharedIdsCount: 0,
  principalSharedIdsCount: 0,
  nonPrincipalSharedIdsCount: 0,
  sourceOnlyCount: 0,
  ontologyOnlyCount: 0,
  sharedOverflow: 0,
  shared: []
});
const createOntologyMissingState = () => ({
  hasCompared: false,
  sourceUniqueIds: 0,
  ontologyFilesCount: 0,
  coveredIdsCount: 0,
  missingIdsCount: 0,
  missingOverflow: 0,
  missing: []
});

const GRAPH_DRAG_THRESHOLD_PX = 3;
const createGraphPanState = () => ({
  isActive: false,
  startX: 0,
  startY: 0,
  scrollLeft: 0,
  scrollTop: 0,
  moved: false
});
const createGraphNodeDragState = () => ({
  isActive: false,
  nodeId: '',
  startX: 0,
  startY: 0,
  originX: 0,
  originY: 0,
  moved: false
});

function SemanticExplorerTab({ showSuccess, showError }) {
  const [viewMode, setViewMode] = useState('verification');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPrepared, setIsPrepared] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [verificationTreeData, setVerificationTreeData] = useState([]);
  const [verificationSummary, setVerificationSummary] = useState({ plans: 0, phases: 0, methods: 0 });
  const [requirementsData, setRequirementsData] = useState([]);
  const [requirementsSummary, setRequirementsSummary] = useState({ requirements: 0, annotationFields: 0, controlFields: 0 });
  const [ontologiesData, setOntologiesData] = useState([]);
  const [ontologiesSummary, setOntologiesSummary] = useState({
    ontologies: 0,
    withDate: 0,
    withVersionInfo: 0,
    withPrefLabel: 0
  });
  const [sourceTtlText, setSourceTtlText] = useState('');
  const [selectedOntologyFile, setSelectedOntologyFile] = useState(null);
  const [isOntologyMatchProcessing, setIsOntologyMatchProcessing] = useState(false);
  const [ontologyMatchState, setOntologyMatchState] = useState(createOntologyMatchState());
  const [selectedOntologyMissingFiles, setSelectedOntologyMissingFiles] = useState([]);
  const [isOntologyMissingProcessing, setIsOntologyMissingProcessing] = useState(false);
  const [ontologyMissingState, setOntologyMissingState] = useState(createOntologyMissingState());
  const [classesData, setClassesData] = useState([]);
  const [classesSummary, setClassesSummary] = useState({ classes: 0, inheritanceLinks: 0, roots: 0 });
  const [classInstancesData, setClassInstancesData] = useState([]);
  const [objectsData, setObjectsData] = useState([]);
  const [objectsSummary, setObjectsSummary] = useState({
    objects: 0,
    realObjects: 0,
    spatialRegions: 0,
    withClassAndSubclass: 0
  });
  const [isObjectGraphDialogOpen, setIsObjectGraphDialogOpen] = useState(false);
  const [isClassInheritanceGraphDialogOpen, setIsClassInheritanceGraphDialogOpen] = useState(false);
  const [selectedGeneralNodeId, setSelectedGeneralNodeId] = useState('');
  const [generalFocusAnchorNodeId, setGeneralFocusAnchorNodeId] = useState('');
  const [generalObjectFocusMode, setGeneralObjectFocusMode] = useState(false);
  const [showAllGeneralNodes, setShowAllGeneralNodes] = useState(false);
  const [selectedPhaseFilterId, setSelectedPhaseFilterId] = useState('');
  const [selectedMethodFilterId, setSelectedMethodFilterId] = useState('');
  const [expandedClassRoots, setExpandedClassRoots] = useState({
    'nen2660:RealObject': true,
    'nen2660:SpatialRegion': true
  });
  const [expandedClassCards, setExpandedClassCards] = useState({});
  const [expandedObjectCards, setExpandedObjectCards] = useState({});
  const [expandedObjectRoots, setExpandedObjectRoots] = useState({
    'nen2660:RealObject': true,
    'nen2660:SpatialRegion': true
  });
  const [expandedObjectClasses, setExpandedObjectClasses] = useState({});
  const [expandedRequirements, setExpandedRequirements] = useState({});
  const [expandedVakgebiedGroups, setExpandedVakgebiedGroups] = useState({});
  const [expandedPhases, setExpandedPhases] = useState({});
  const [expandedMethods, setExpandedMethods] = useState({});
  const graphContainerRef = useRef(null);
  const graphPanRef = useRef(createGraphPanState());
  const graphNodeDragRef = useRef(createGraphNodeDragState());
  const graphSuppressClickRef = useRef(false);
  const [freeNodePositions, setFreeNodePositions] = useState({});
  const [isGraphDragging, setIsGraphDragging] = useState(false);
  const [draggingNodeId, setDraggingNodeId] = useState('');

  const normalizeId = (value) => value?.trim() || '';

  const getTTLBlocks = (ttlText) =>
    ttlText
      .split(/\n\s*\n/)
      .map((block) => block.trim())
      .filter(Boolean);

  const extractQuotedValues = (text) => {
    const values = [];
    const valueRegex = /"((?:[^"\\]|\\.)*)"/g;
    let match;

    while ((match = valueRegex.exec(text)) !== null) {
      values.push(match[1].replace(/\\"/g, '"'));
    }

    return values;
  };

  const extractRdfTypesFromBlock = (block) => {
    const rdfTypes = [];
    // Stop type capture at real predicate/statement boundaries, not at dots inside URLs.
    // Supports both explicit rdf:type and Turtle shorthand "a".
    const rdfTypeRegex = /(?:^|[\s;])(?:rdf:type|a)\s+([\s\S]*?)(?=\s*;\s*(?:[A-Za-z][\w-]*:|<[^>]+>)|\s*\.\s*(?:\n|$))/g;
    const typeRegex = /(<[^>]+>|[A-Za-z][\w-]*:[^\s,;.]+)/g;
    let rdfTypeMatch;

    while ((rdfTypeMatch = rdfTypeRegex.exec(block)) !== null) {
      let typeMatch;
      while ((typeMatch = typeRegex.exec(rdfTypeMatch[1])) !== null) {
        rdfTypes.push(normalizeId(typeMatch[1]));
      }
    }

    return rdfTypes;
  };

  const blockHasType = (block, typeId) => extractRdfTypesFromBlock(block).includes(typeId);

  const getMethodKey = (phaseId, methodId) => `${phaseId}::${methodId}`;
  const getObjectClassKey = (rootId, classId) => `${rootId}::${classId}`;

  const buildExpandedPhasesState = (phases, value) =>
    phases.reduce((acc, phaseNode) => {
      acc[phaseNode.id] = value;
      return acc;
    }, {});

  const buildExpandedMethodsState = (phases, value) => {
    const next = {};
    phases.forEach((phaseNode) => {
      phaseNode.methods.forEach((methodNode) => {
        next[getMethodKey(phaseNode.id, methodNode.id)] = value;
      });
    });
    return next;
  };

  const extractLabelMap = (ttlText) => {
    const labels = {};
    const blocks = getTTLBlocks(ttlText);

    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      const labelMatch = block.match(/skos:prefLabel\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);

      if (!subjectMatch || !labelMatch) return;

      const key = normalizeId(subjectMatch[1]);
      const value = labelMatch[1].replace(/\\"/g, '"');
      labels[key] = value;
    });

    return labels;
  };

  const extractPropertyDefinitions = (ttlText) => {
    const blocks = getTTLBlocks(ttlText);
    const definitions = {};

    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*(tennet:[^\s]+)/);
      if (!subjectMatch) return;

      const subjectId = normalizeId(subjectMatch[1]);
      const labelMatch = block.match(/skos:prefLabel\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      if (!labelMatch) return;

      const label = labelMatch[1].replace(/\\"/g, '"');
      const isAnnotation = blockHasType(block, 'owl:AnnotationProperty');
      const noteMatch = block.match(/skos:note\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      const noteTypeLabel = noteMatch ? noteMatch[1].replace(/\\"/g, '"').trim() : '';

      definitions[subjectId] = {
        id: subjectId,
        label,
        typeLabel: isAnnotation ? 'AnnotationProperty' : (noteTypeLabel || 'Control')
      };
    });

    return definitions;
  };

  const parseVerificationPlans = (ttlText, labelMap) => {
    const blocks = getTTLBlocks(ttlText);
    const phaseMap = new Map();
    let totalPlans = 0;

    const entityTypesMap = {};
    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      if (!subjectMatch) return;

      const typeIds = extractRdfTypesFromBlock(block);
      if (typeIds.length === 0) return;

      entityTypesMap[normalizeId(subjectMatch[1])] = typeIds;
    });

    blocks.forEach((block) => {
      if (!blockHasType(block, 'tennet:VerificationPlan')) {
        return;
      }

      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      if (!subjectMatch) {
        return;
      }

      const subject = normalizeId(subjectMatch[1]);
      const phaseMatch = block.match(/tennet:hasPhase\s+((?:<[^>]+>|tennet:[^\s;]+))\s*[;.]?/);
      const methodMatch = block.match(/tennet:hasVerificationMethod\s+((?:<[^>]+>|tennet:[^\s;]+))\s*[;.]?/);
      const hasAsSubjectMatch = block.match(/tennet:hasAsSubject\s+((?:<[^>]+>|tennet:[^\s;]+))\s*[;.]?/);
      const isVerificationOfMatch = block.match(/tennet:isVerificationOf\s+((?:<[^>]+>|tennet:[^\s;]+))\s*[;.]?/);

      const phaseId = normalizeId(phaseMatch?.[1]) || '__SIN_FASE__';
      const methodId = normalizeId(methodMatch?.[1]) || '__SIN_METODO__';
      const hasAsSubjectId = normalizeId(hasAsSubjectMatch?.[1]);
      const isVerificationOfId = normalizeId(isVerificationOfMatch?.[1]);

      totalPlans += 1;

      if (!phaseMap.has(phaseId)) {
        phaseMap.set(phaseId, {
          id: phaseId,
          label: labelMap[phaseId] || (phaseId === '__SIN_FASE__' ? 'Sin fase' : phaseId),
          methods: new Map(),
          plansCount: 0
        });
      }

      const phaseNode = phaseMap.get(phaseId);
      phaseNode.plansCount += 1;

      if (!phaseNode.methods.has(methodId)) {
        phaseNode.methods.set(methodId, {
          id: methodId,
          label: labelMap[methodId] || (methodId === '__SIN_METODO__' ? 'Sin metodo' : methodId),
          plans: []
        });
      }

      phaseNode.methods.get(methodId).plans.push({
        id: subject,
        hasAsSubject: {
          id: hasAsSubjectId,
          label: labelMap[hasAsSubjectId] || hasAsSubjectId || '-',
          types: (entityTypesMap[hasAsSubjectId] || []).map((typeId) => ({
            id: typeId,
            label: labelMap[typeId] || typeId
          }))
        },
        isVerificationOf: {
          id: isVerificationOfId,
          label: labelMap[isVerificationOfId] || isVerificationOfId || '-'
        }
      });
    });

    const extractPhaseNumber = (label) => {
      const match = label.match(/fase\s*:\s*(\d+(?:[.,]\d+)?)/i);
      if (!match) return null;
      return Number(match[1].replace(',', '.'));
    };

    const phases = Array.from(phaseMap.values()).map((phaseNode) => ({
      ...phaseNode,
      methods: Array.from(phaseNode.methods.values())
    }));

    phases.sort((a, b) => {
      const aNum = extractPhaseNumber(a.label);
      const bNum = extractPhaseNumber(b.label);

      if (aNum !== null && bNum !== null) return aNum - bNum;
      if (aNum !== null) return -1;
      if (bNum !== null) return 1;

      return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
    });

    const methodsCount = phases.reduce((acc, phaseNode) => acc + phaseNode.methods.length, 0);

    return {
      phases,
      summary: {
        plans: totalPlans,
        phases: phases.length,
        methods: methodsCount
      }
    };
  };

  const parseClasses = (ttlText, labelMap, propertyDefinitions) => {
    const blocks = getTTLBlocks(ttlText);
    const classes = [];

    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      if (!subjectMatch) return;

      const rdfTypes = extractRdfTypesFromBlock(block);

      if (!rdfTypes.includes('owl:Class') || !rdfTypes.includes('sh:NodeShape')) return;

      const classId = normalizeId(subjectMatch[1]);
      const prefLabelMatch = block.match(/skos:prefLabel\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      const classCodeMatch = block.match(/tennet:8d464b9745fc848aca8ae713a8dcf536\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);

      const subClassIds = new Set();
      const subClassRegex = /rdfs:subClassOf\s+([\s\S]*?)\s*[;.]/g;
      let subClassMatch;
      while ((subClassMatch = subClassRegex.exec(block)) !== null) {
        const rawClause = subClassMatch[1];
        const classOnlyClause = rawClause.split('[')[0].trim();
        if (!classOnlyClause) continue;

        const parentRegex = /(<[^>]+>|[A-Za-z][\w-]*:[^\s,;\]]+)/g;
        let parentMatch;
        while ((parentMatch = parentRegex.exec(classOnlyClause)) !== null) {
          const parentId = normalizeId(parentMatch[1]);
          if (!parentId) continue;
          if (parentId === 'rdf:type') continue;
          if (parentId.startsWith('owl:')) continue;
          if (parentId.startsWith('sh:')) continue;
          subClassIds.add(parentId);
        }
      }

      const requirements = [];
      const requirementsMatch = block.match(/nen2660:hasRequirement\s+([\s\S]*?)(?:\s*;\s*(?:\n|$)|\s*\.\s*(?:\n|$))/);
      if (requirementsMatch) {
        const requirementRegex = /(<[^>]+>|tennet:[^\s,;]+)/g;
        let requirementMatch;
        while ((requirementMatch = requirementRegex.exec(requirementsMatch[1])) !== null) {
          const requirementId = normalizeId(requirementMatch[1]);
          requirements.push({
            id: requirementId,
            label: labelMap[requirementId] || requirementId
          });
        }
      }

      const properties = [];
      const propertyRegex = /sh:property\s+\[([\s\S]*?)\]\s*[;.]?/g;
      let propertyMatch;
      while ((propertyMatch = propertyRegex.exec(block)) !== null) {
        const propertyBlock = propertyMatch[1];
        const pathMatch = propertyBlock.match(/sh:path\s+(<[^>]+>|[A-Za-z][\w-]*:[^\s;\]]+)/);
        if (!pathMatch) continue;

        const pathId = normalizeId(pathMatch[1]);
        const pathDefinition = propertyDefinitions[pathId];
        const minCountMatch = propertyBlock.match(/sh:minCount\s+(\d+)/);
        const hasValueMatch = propertyBlock.match(/sh:hasValue\s+("([^"\\]*(?:\\.[^"\\]*)*)"|<[^>]+>|[A-Za-z][\w-]*:[^\s;\]]+)/);
        const classMatch = propertyBlock.match(/sh:class\s+(<[^>]+>|[A-Za-z][\w-]*:[^\s;\]]+)/);

        let hasValue = '';
        if (hasValueMatch) {
          if (hasValueMatch[2] !== undefined) {
            hasValue = hasValueMatch[2].replace(/\\"/g, '"');
          } else {
            const rawHasValueId = normalizeId(hasValueMatch[1]);
            hasValue = labelMap[rawHasValueId] || rawHasValueId;
          }
        }

        const classIdValue = classMatch ? normalizeId(classMatch[1]) : '';
        properties.push({
          pathId,
          pathLabel: pathDefinition?.label || labelMap[pathId] || pathId,
          minCount: minCountMatch ? Number(minCountMatch[1]) : null,
          hasValue,
          classId: classIdValue,
          classLabel: classIdValue ? (labelMap[classIdValue] || classIdValue) : ''
        });
      }

      classes.push({
        id: classId,
        label: prefLabelMatch ? prefLabelMatch[1].replace(/\\"/g, '"') : (labelMap[classId] || classId),
        classCodePropertyId: CLASS_CODE_PROPERTY_ID,
        classCodePropertyLabel: propertyDefinitions[CLASS_CODE_PROPERTY_ID]?.label || 'Class code RDS-PS',
        classCode: classCodeMatch ? classCodeMatch[1].replace(/\\"/g, '"') : '',
        subClassOfIds: Array.from(subClassIds),
        requirements,
        properties
      });
    });

    const classIdSet = new Set(classes.map((item) => item.id));
    const normalizedClasses = classes
      .map((item) => ({
        id: item.id,
        label: item.label,
        classCodePropertyId: item.classCodePropertyId,
        classCodePropertyLabel: item.classCodePropertyLabel,
        classCode: item.classCode,
        requirements: item.requirements,
        properties: item.properties,
        subClassOf: item.subClassOfIds.map((parentId) => ({
          id: parentId,
          label: labelMap[parentId] || parentId,
          isKnownClass: classIdSet.has(parentId)
        }))
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

    const inheritanceLinks = normalizedClasses.reduce((acc, item) => acc + item.subClassOf.length, 0);
    const roots = normalizedClasses.filter((item) => !item.subClassOf.some((parent) => parent.isKnownClass)).length;

    return {
      classes: normalizedClasses,
      summary: {
        classes: normalizedClasses.length,
        inheritanceLinks,
        roots
      }
    };
  };

  const parseObjects = (ttlText, classes, labelMap, propertyDefinitions) => {
    const blocks = getTTLBlocks(ttlText);
    const classIdSet = new Set(classes.map((item) => item.id));
    const classMap = {};
    classes.forEach((item) => {
      classMap[item.id] = item;
    });

    // Include class-like resources that may not have sh:NodeShape but still appear in rdf:type chains.
    const classLikeMap = {};
    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      if (!subjectMatch) return;

      const subjectId = normalizeId(subjectMatch[1]);
      const rdfTypes = extractRdfTypesFromBlock(block);

      const hasSubClassOf = /rdfs:subClassOf\s+/.test(block);
      if (!rdfTypes.includes('owl:Class') && !hasSubClassOf) return;

      const parentIds = new Set();
      const subClassRegex = /rdfs:subClassOf\s+([\s\S]*?)\s*[;.]/g;
      let subClassMatch;
      while ((subClassMatch = subClassRegex.exec(block)) !== null) {
        const rawClause = subClassMatch[1];
        const classOnlyClause = rawClause.split('[')[0].trim();
        if (!classOnlyClause) continue;

        const parentRegex = /(<[^>]+>|[A-Za-z][\w-]*:[^\s,;\]]+)/g;
        let parentMatch;
        while ((parentMatch = parentRegex.exec(classOnlyClause)) !== null) {
          const parentId = normalizeId(parentMatch[1]);
          if (!parentId) continue;
          if (parentId === 'rdf:type') continue;
          if (parentId.startsWith('owl:')) continue;
          if (parentId.startsWith('sh:')) continue;
          parentIds.add(parentId);
        }
      }

      const prefLabelMatch = block.match(/skos:prefLabel\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      classLikeMap[subjectId] = {
        id: subjectId,
        label: prefLabelMatch ? prefLabelMatch[1].replace(/\\"/g, '"') : (labelMap[subjectId] || subjectId),
        parentIds: Array.from(parentIds)
      };
    });

    const objects = [];

    const parseValueTokens = (rawText) => {
      const values = [];
      const valueRegex = /"((?:[^"\\]|\\.)*)"|(<[^>]+>|[A-Za-z][\w-]*:[^\s,;]+)/g;
      let valueMatch;

      while ((valueMatch = valueRegex.exec(rawText)) !== null) {
        if (valueMatch[1] !== undefined) {
          const literalValue = valueMatch[1].replace(/\\"/g, '"');
          values.push({
            id: '',
            label: literalValue,
            value: literalValue,
            isLiteral: true
          });
          continue;
        }

        const valueId = normalizeId(valueMatch[2]);
        values.push({
          id: valueId,
          label: labelMap[valueId] || valueId,
          value: valueId,
          isLiteral: false
        });
      }

      return values;
    };

    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      if (!subjectMatch) return;

      const subjectId = normalizeId(subjectMatch[1]);
      if (classIdSet.has(subjectId)) return;

      const rdfTypes = extractRdfTypesFromBlock(block);

      if (rdfTypes.includes('owl:Class')) return;

      const rootTypeIds = [];
      if (rdfTypes.includes('nen2660:RealObject')) rootTypeIds.push('nen2660:RealObject');
      if (rdfTypes.includes('nen2660:SpatialRegion')) rootTypeIds.push('nen2660:SpatialRegion');

      const nonStructuralTypeIds = rdfTypes.filter((typeId) =>
        typeId
        && typeId !== 'nen2660:RealObject'
        && typeId !== 'nen2660:SpatialRegion'
        && !typeId.startsWith('owl:')
        && !typeId.startsWith('rdf:')
        && !typeId.startsWith('rdfs:')
        && !typeId.startsWith('sh:')
        && !typeId.startsWith('skos:')
      );

      const mappedClassTypeIds = nonStructuralTypeIds.filter((typeId) => classIdSet.has(typeId) || !!classLikeMap[typeId]);
      const classTypeIds = Array.from(new Set((mappedClassTypeIds.length > 0 ? mappedClassTypeIds : nonStructuralTypeIds)));

      if (classTypeIds.length === 0 && rootTypeIds.length === 0) return;

      const getClassParentIds = (classId) => {
        const parsedParents = (classMap[classId]?.subClassOf || []).map((parent) => parent.id);
        if (parsedParents.length > 0) return parsedParents;
        return classLikeMap[classId]?.parentIds || [];
      };

      const getClassLabel = (classId) => (
        classMap[classId]?.label
        || classLikeMap[classId]?.label
        || labelMap[classId]
        || classId
      );

      const sortedClassTypeIds = [...classTypeIds].sort((a, b) => {
        const aParents = getClassParentIds(a);
        const bParents = getClassParentIds(b);

        if (aParents.includes(b)) return 1;
        if (bParents.includes(a)) return -1;

        return getClassLabel(a).localeCompare(getClassLabel(b), 'es', { sensitivity: 'base' });
      });

      const classParentId = sortedClassTypeIds.find((candidateId) =>
        sortedClassTypeIds.some((otherId) =>
          otherId !== candidateId && getClassParentIds(otherId).includes(candidateId)
        )
      ) || '';

      const classChildId = sortedClassTypeIds.find((candidateId) =>
        classParentId
        && candidateId !== classParentId
        && getClassParentIds(candidateId).includes(classParentId)
      ) || '';

      const prefLabelMatch = block.match(/skos:prefLabel\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      const notationMatch = block.match(/skos:notation\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);

      const attributes = [];
      const predicateRegex = /(?:^|\n)\s*(tennet:[^\s;]+)\s+([\s\S]*?)(?=\s*;\s*(?:\n|$)|\s*\.\s*(?:\n|$))/g;
      let predicateMatch;

      while ((predicateMatch = predicateRegex.exec(block)) !== null) {
        const predicateId = normalizeId(predicateMatch[1]);
        if (predicateId === subjectId) continue;
        const rawValue = predicateMatch[2];

        const values = parseValueTokens(rawValue)
          .filter((value) => !!value.label);

        if (values.length === 0) continue;

        const predicateDefinition = propertyDefinitions[predicateId];
        attributes.push({
          id: predicateId,
          label: predicateDefinition?.label || labelMap[predicateId] || predicateId,
          typeLabel: predicateDefinition?.typeLabel || '',
          values
        });
      }

      attributes.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

      objects.push({
        id: subjectId,
        notation: notationMatch ? notationMatch[1].replace(/\\"/g, '"') : '',
        label: prefLabelMatch ? prefLabelMatch[1].replace(/\\"/g, '"') : (labelMap[subjectId] || subjectId),
        classTypeIds: sortedClassTypeIds,
        classTypes: sortedClassTypeIds.map((classId) => ({
          id: classId,
          label: getClassLabel(classId)
        })),
        rootTypeIds,
        rootTypes: rootTypeIds.map((rootId) => ({
          id: rootId,
          label: labelMap[rootId] || rootId.replace('nen2660:', '')
        })),
        classParent: classParentId
          ? { id: classParentId, label: getClassLabel(classParentId) }
          : null,
        classChild: classChildId
          ? { id: classChildId, label: getClassLabel(classChildId) }
          : null,
        attributes
      });
    });

    objects.sort((a, b) => {
      if (a.notation && b.notation) {
        return a.notation.localeCompare(b.notation, 'es', { numeric: true, sensitivity: 'base' });
      }
      if (a.notation) return -1;
      if (b.notation) return 1;
      return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
    });

    const classInstances = objects.map((item) => ({
      id: item.id,
      label: item.label,
      notation: item.notation,
      classTypeIds: item.classTypeIds
    }));

    return {
      objects,
      classInstances,
      summary: {
        objects: objects.length,
        realObjects: objects.filter((item) => item.rootTypeIds.includes('nen2660:RealObject')).length,
        spatialRegions: objects.filter((item) => item.rootTypeIds.includes('nen2660:SpatialRegion')).length,
        withClassAndSubclass: objects.filter((item) => item.classParent && item.classChild).length
      }
    };
  };

  const extractDocumentMap = (ttlText) => {
    const documents = {};
    const blocks = getTTLBlocks(ttlText);

    // First pass: build DocumentVersion → seeAlso URL map
    const versionUrlMap = {};
    blocks.forEach((block) => {
      if (!blockHasType(block, 'tennet:DocumentVersion')) return;
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      if (!subjectMatch) return;
      const versionId = normalizeId(subjectMatch[1]);
      const seeAlsoMatch = block.match(/rdfs:seeAlso\s+"([^"\\]*(?:\\.[^"\\]*)*)"/s);
      if (seeAlsoMatch) {
        versionUrlMap[versionId] = seeAlsoMatch[1].replace(/\\"/g, '"').trim();
      }
    });

    // Second pass: extract Document blocks with optional DocumentVersion URL
    blocks.forEach((block) => {
      if (!blockHasType(block, 'tennet:Document')) return;

      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      if (!subjectMatch) return;

      const docId = normalizeId(subjectMatch[1]);
      const prefLabelMatch = block.match(/skos:prefLabel\s+"([^"\\]*(?:\\.[^"\\]*)*)"/s);
      const definitionMatch = block.match(/skos:definition\s+"([^"\\]*(?:\\.[^"\\]*)*)"/s);

      const docVersionMatch = block.match(/tennet:DocumentVersion\s+(<[^>]+>|tennet:[^\s;.]+)/);
      let seeAlso = '';
      if (docVersionMatch) {
        const versionId = normalizeId(docVersionMatch[1]);
        seeAlso = versionUrlMap[versionId] || '';
      }

      documents[docId] = {
        id: docId,
        name: prefLabelMatch ? prefLabelMatch[1].replace(/\\"/g, '"') : docId,
        definition: definitionMatch ? definitionMatch[1].replace(/\\"/g, '"') : '',
        seeAlso
      };
    });

    return documents;
  };

  const parseRequirements = (ttlText, propertyDefinitions, documentMap) => {
    const blocks = getTTLBlocks(ttlText);
    const requirements = [];

    let annotationFields = 0;
    let controlFields = 0;

    const ignoredPredicates = new Set([
      'tennet:hasAsSource',
      'tennet:isDerivedFrom',
      'tennet:hasAsSubject',
      'tennet:hasPhase',
      'tennet:hasVerificationMethod',
      'tennet:isVerificationOf'
    ]);

    blocks.forEach((block) => {
      if (!blockHasType(block, 'nen2660:Requirement')) {
        return;
      }

      const subjectMatch = block.match(/^\s*((?:<[^>]+>|tennet:[^\s]+))/);
      if (!subjectMatch) {
        return;
      }

      const requirementId = normalizeId(subjectMatch[1]);
      const notationMatch = block.match(/skos:notation\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      const prefLabelMatch = block.match(/skos:prefLabel\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      const rdfValueMatch = block.match(/rdf:value\s+"([\s\S]*?)"\s*[;.]?/);

      const fields = [];
      const fieldRegex = /(tennet:[^\s;]+)\s+((?:"(?:[^"\\]|\\.)*"(?:\s*,\s*"(?:[^"\\]|\\.)*")*\s*)+)\s*[;.]?/g;
      let fieldMatch;

      while ((fieldMatch = fieldRegex.exec(block)) !== null) {
        const predicateId = normalizeId(fieldMatch[1]);
        if (ignoredPredicates.has(predicateId)) continue;

        const values = extractQuotedValues(fieldMatch[2]);
        if (values.length === 0) continue;

        const definition = propertyDefinitions[predicateId];
        const typeLabel = definition?.typeLabel || 'Control';

        if (typeLabel === 'AnnotationProperty') {
          annotationFields += 1;
        } else {
          controlFields += 1;
        }

        fields.push({
          id: predicateId,
          fieldName: definition?.label || predicateId,
          typeLabel,
          values
        });
      }

      const refersToMatch = block.match(/tennet:refersTo\s+((?:<[^>]+>(?:\s*,\s*<[^>]+>)*))/s);
      const refersToDocuments = [];
      if (refersToMatch) {
        const uriRegex = /<([^>]+)>/g;
        let uriMatch;
        while ((uriMatch = uriRegex.exec(refersToMatch[1])) !== null) {
          const docId = `<${uriMatch[1]}>`;
          refersToDocuments.push(documentMap[docId] || { id: docId, name: docId, definition: '' });
        }
      }

      const hasAsSourceMatch = block.match(/tennet:hasAsSource\s+((?:<[^>]+>(?:\s*,\s*<[^>]+>)*))/s);
      const hasAsSourceDocuments = [];
      if (hasAsSourceMatch) {
        const uriRegex = /<([^>]+)>/g;
        let uriMatch;
        while ((uriMatch = uriRegex.exec(hasAsSourceMatch[1])) !== null) {
          const docId = `<${uriMatch[1]}>`;
          hasAsSourceDocuments.push(documentMap[docId] || { id: docId, name: docId, definition: '' });
        }
      }

      requirements.push({
        id: requirementId,
        notation: notationMatch ? notationMatch[1].replace(/\\"/g, '"') : '-',
        name: prefLabelMatch ? prefLabelMatch[1].replace(/\\"/g, '"') : requirementId,
        norm: rdfValueMatch ? rdfValueMatch[1].replace(/\\"/g, '"') : '-',
        fields,
        refersToDocuments,
        hasAsSourceDocuments
      });
    });

    requirements.sort((a, b) => a.notation.localeCompare(b.notation, 'es', { numeric: true, sensitivity: 'base' }));

    return {
      requirements,
      summary: {
        requirements: requirements.length,
        annotationFields,
        controlFields
      }
    };
  };

  const parseOntologies = (ttlText, labelMap) => {
    const blocks = getTTLBlocks(ttlText);
    const ontologyMap = new Map();

    const parseMetadataValues = (rawValue) => {
      const values = [];
      const valueRegex = /"((?:[^"\\]|\\.)*)"(?:\^\^[^\s,;]+)?|(<[^>]+>|[A-Za-z][\w-]*:[^\s,;]+)/g;
      let valueMatch;

      while ((valueMatch = valueRegex.exec(rawValue)) !== null) {
        if (valueMatch[1] !== undefined) {
          values.push(valueMatch[1].replace(/\\"/g, '"'));
          continue;
        }

        const valueId = normalizeId(valueMatch[2]);
        values.push(labelMap[valueId] || valueId);
      }

      return values;
    };

    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|[A-Za-z][\w-]*:[^\s]+))/);
      if (!subjectMatch) return;

      const subjectId = normalizeId(subjectMatch[1]);
      const rdfTypes = extractRdfTypesFromBlock(block);
      if (!rdfTypes.includes('owl:Ontology')) return;

      const metadata = [];
      const metadataRegex = /(?:^|\n)\s*([A-Za-z][\w-]*:[^\s;]+|<[^>]+>)\s+([\s\S]*?)(?=\s*;\s*(?:\n|$)|\s*\.\s*(?:\n|$))/g;
      let metadataMatch;

      while ((metadataMatch = metadataRegex.exec(block)) !== null) {
        const predicateId = normalizeId(metadataMatch[1]);
        if (!predicateId || predicateId === subjectId || predicateId === 'rdf:type' || predicateId === 'a') continue;

        const values = parseMetadataValues(metadataMatch[2]);
        if (values.length === 0) continue;

        metadata.push({
          id: predicateId,
          label: labelMap[predicateId] || predicateId,
          values
        });
      }

      const getFirstMetadataValue = (predicateId) =>
        metadata.find((item) => item.id === predicateId)?.values?.[0] || '';

      ontologyMap.set(subjectId, {
        id: subjectId,
        prefLabel: getFirstMetadataValue('skos:prefLabel') || labelMap[subjectId] || subjectId,
        date: getFirstMetadataValue('dcterms:date'),
        versionInfo: getFirstMetadataValue('owl:versionInfo'),
        metadata
      });
    });

    const ontologies = Array.from(ontologyMap.values())
      .sort((a, b) => {
        const byLabel = (a.prefLabel || '').localeCompare(b.prefLabel || '', 'es', { sensitivity: 'base' });
        if (byLabel !== 0) return byLabel;
        return a.id.localeCompare(b.id, 'es', { sensitivity: 'base' });
      });

    return {
      ontologies,
      summary: {
        ontologies: ontologies.length,
        withDate: ontologies.filter((item) => !!item.date).length,
        withVersionInfo: ontologies.filter((item) => !!item.versionInfo).length,
        withPrefLabel: ontologies.filter((item) => !!item.prefLabel).length
      }
    };
  };

  const extractComparableOntologyId = (rawValue) => {
    const normalizedValue = normalizeId(rawValue).replace(/^<|>$/g, '');
    if (!normalizedValue) return '';

    const uuidMatch = normalizedValue.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
    if (!uuidMatch) return '';

    return uuidMatch[0].toLowerCase();
  };

  const buildUuidIndexFromTtl = (ttlText) => {
    const index = {};
    const tokenRegex = /<[^>]+>|[A-Za-z][\w-]*:[^\s,;.]+|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
    let tokenMatch;

    while ((tokenMatch = tokenRegex.exec(ttlText)) !== null) {
      const rawToken = normalizeId(tokenMatch[0]);
      const comparableId = extractComparableOntologyId(rawToken);
      if (!comparableId) continue;

      if (!index[comparableId]) {
        index[comparableId] = new Set();
      }
      index[comparableId].add(rawToken);
    }

    return index;
  };

  const buildSubjectUuidIndexFromTtl = (ttlText) => {
    const index = {};
    const blocks = getTTLBlocks(ttlText || '');

    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|[A-Za-z][\w-]*:[^\s]+))/);
      if (!subjectMatch) return;

      const subjectId = normalizeId(subjectMatch[1]);
      const comparableId = extractComparableOntologyId(subjectId);
      if (!comparableId) return;

      if (!index[comparableId]) {
        index[comparableId] = new Set();
      }
      index[comparableId].add(subjectId);
    });

    return index;
  };

  const buildMatchedIdContexts = (ttlText, targetIdSet) => {
    const contextsById = {};
    const blocks = getTTLBlocks(ttlText || '');

    blocks.forEach((block) => {
      const subjectMatch = block.match(/^\s*((?:<[^>]+>|[A-Za-z][\w-]*:[^\s]+))/);
      if (!subjectMatch) return;

      const subjectId = normalizeId(subjectMatch[1]);
      const subjectComparableId = extractComparableOntologyId(subjectId);
      const prefLabelMatch = block.match(/skos:prefLabel\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      const notationMatch = block.match(/skos:notation\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      const dateMatch = block.match(/dcterms:date\s+"([^"\\]*(?:\\.[^"\\]*)*)"(?:\^\^[^\s;,.]+)?\s*[;.]?/);
      const versionInfoMatch = block.match(/owl:versionInfo\s+"([^"\\]*(?:\\.[^"\\]*)*)"\s*[;.]?/);
      const rdfTypes = extractRdfTypesFromBlock(block);

      const tokenRegex = /<[^>]+>|[A-Za-z][\w-]*:[^\s,;.]+|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
      const matchedTokensById = {};
      let tokenMatch;

      while ((tokenMatch = tokenRegex.exec(block)) !== null) {
        const rawToken = normalizeId(tokenMatch[0]);
        const comparableId = extractComparableOntologyId(rawToken);
        if (!comparableId || !targetIdSet.has(comparableId)) continue;

        if (!matchedTokensById[comparableId]) {
          matchedTokensById[comparableId] = new Set();
        }
        matchedTokensById[comparableId].add(rawToken);
      }

      Object.entries(matchedTokensById).forEach(([comparableId, tokenSet]) => {
        if (!contextsById[comparableId]) {
          contextsById[comparableId] = [];
        }

        const nextContext = {
          subjectId,
          prefLabel: prefLabelMatch ? prefLabelMatch[1].replace(/\\"/g, '"') : '',
          notation: notationMatch ? notationMatch[1].replace(/\\"/g, '"') : '',
          date: dateMatch ? dateMatch[1].replace(/\\"/g, '"') : '',
          versionInfo: versionInfoMatch ? versionInfoMatch[1].replace(/\\"/g, '"') : '',
          rdfTypes,
          isSubjectMatch: subjectComparableId === comparableId,
          matchedTokens: Array.from(tokenSet).slice(0, 6),
          blockPreview: block.split('\n').slice(0, 8).join('\n').trim()
        };

        const contexts = contextsById[comparableId];
        const hasSubjectContext = contexts.some((ctx) => ctx.isSubjectMatch);

        if (contexts.length < 4) {
          contexts.push(nextContext);
          return;
        }

        // Even when we reached the cap, keep at least one subject-principal context.
        if (nextContext.isSubjectMatch && !hasSubjectContext) {
          const replaceIndex = contexts.findIndex((ctx) => !ctx.isSubjectMatch);
          if (replaceIndex >= 0) {
            contexts[replaceIndex] = nextContext;
          }
        }
      });
    });

    return contextsById;
  };

  const buildOntologyMatchResults = (sourceText, ontologyText) => {
    // Prefer principal subject matches, but also include UUID matches from references.
    const sourceSubjectIndex = buildSubjectUuidIndexFromTtl(sourceText || '');
    const ontologySubjectIndex = buildSubjectUuidIndexFromTtl(ontologyText || '');
    const sourceIndex = buildUuidIndexFromTtl(sourceText || '');
    const ontologyIndex = buildUuidIndexFromTtl(ontologyText || '');

    const sourceSubjectIds = Object.keys(sourceSubjectIndex);
    const ontologySubjectIds = Object.keys(ontologySubjectIndex);
    const sourceIds = Object.keys(sourceIndex);
    const ontologyIds = Object.keys(ontologyIndex);

    const ontologySubjectIdSet = new Set(ontologySubjectIds);
    const ontologyIdSet = new Set(ontologyIds);
    const sourceIdSet = new Set(sourceIds);

    const sharedPrincipalIds = sourceSubjectIds
      .filter((id) => ontologySubjectIdSet.has(id))
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
    const sharedPrincipalIdSet = new Set(sharedPrincipalIds);

    const sharedReferencedIds = sourceIds
      .filter((id) => ontologyIdSet.has(id) && !sharedPrincipalIdSet.has(id))
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));

    const sharedIds = [...sharedPrincipalIds, ...sharedReferencedIds];

    const sharedIdsForRendering = sharedIds.slice(0, MAX_ONTOLOGY_MATCH_RESULTS);
    const sharedIdRenderSet = new Set(sharedIdsForRendering);
    const sourceContextsById = buildMatchedIdContexts(sourceText || '', sharedIdRenderSet);
    const ontologyContextsById = buildMatchedIdContexts(ontologyText || '', sharedIdRenderSet);

    const buildPrioritizedForms = (subjectForms, allForms) => {
      const forms = [];
      const seen = new Set();

      [...subjectForms, ...allForms].forEach((form) => {
        if (!form || seen.has(form) || forms.length >= 4) return;
        seen.add(form);
        forms.push(form);
      });

      return forms;
    };

    const buildPreferredContexts = (contexts) => {
      if (!contexts || contexts.length === 0) return [];

      const subjectContexts = contexts.filter((ctx) => ctx.isSubjectMatch);
      return (subjectContexts.length > 0 ? subjectContexts : contexts).slice(0, 4);
    };

    const shared = sharedIdsForRendering
      .map((id) => {
        const sourceSubjectForms = Array.from(sourceSubjectIndex[id] || []);
        const ontologySubjectForms = Array.from(ontologySubjectIndex[id] || []);
        const sourceAllForms = Array.from(sourceIndex[id] || []);
        const ontologyAllForms = Array.from(ontologyIndex[id] || []);
        const sourceContexts = buildPreferredContexts(sourceContextsById[id] || []);
        const ontologyContexts = buildPreferredContexts(ontologyContextsById[id] || []);

        const sourceHasPrincipal = sourceSubjectForms.length > 0;
        const ontologyHasPrincipal = ontologySubjectForms.length > 0;

        let matchLabel = 'ID coincidente por referencia';
        if (sourceHasPrincipal && ontologyHasPrincipal) {
          matchLabel = 'ID principal coincidente';
        } else if (sourceHasPrincipal || ontologyHasPrincipal) {
          matchLabel = 'ID coincidente (principal en uno de los archivos)';
        }

        return {
          id,
          matchLabel,
          sourceForms: buildPrioritizedForms(sourceSubjectForms, sourceAllForms),
          ontologyForms: buildPrioritizedForms(ontologySubjectForms, ontologyAllForms),
          sourceContexts,
          ontologyContexts
        };
      });

    const sourceOnlyCount = sourceIds.reduce((count, id) => count + (ontologyIdSet.has(id) ? 0 : 1), 0);
    const ontologyOnlyCount = ontologyIds.reduce((count, id) => count + (sourceIdSet.has(id) ? 0 : 1), 0);

    return {
      hasCompared: true,
      sourceUniqueIds: sourceIds.length,
      ontologyUniqueIds: ontologyIds.length,
      sharedIdsCount: sharedIds.length,
      principalSharedIdsCount: sharedPrincipalIds.length,
      nonPrincipalSharedIdsCount: sharedReferencedIds.length,
      sourceOnlyCount,
      ontologyOnlyCount,
      sharedOverflow: Math.max(0, sharedIds.length - MAX_ONTOLOGY_MATCH_RESULTS),
      shared
    };
  };

  const buildOntologyMissingResults = (sourceText, ontologyTexts) => {
    const sourceIndex = buildSubjectUuidIndexFromTtl(sourceText || '');
    const sourceIds = Object.keys(sourceIndex);

    const ontologyUnionIdSet = new Set();
    (ontologyTexts || []).forEach((ontologyText) => {
      const ontologyIndex = buildUuidIndexFromTtl(ontologyText || '');
      Object.keys(ontologyIndex).forEach((id) => {
        ontologyUnionIdSet.add(id);
      });
    });

    const missingIds = sourceIds
      .filter((id) => !ontologyUnionIdSet.has(id))
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));

    const missingIdsForRendering = missingIds.slice(0, MAX_ONTOLOGY_MATCH_RESULTS);
    const missingIdRenderSet = new Set(missingIdsForRendering);
    const sourceContextsById = buildMatchedIdContexts(sourceText || '', missingIdRenderSet);

    const missing = missingIdsForRendering.map((id) => {
      const contexts = sourceContextsById[id] || [];
      const subjectContexts = contexts.filter((ctx) => ctx.isSubjectMatch);

      return {
        id,
        sourceForms: Array.from(sourceIndex[id] || []).slice(0, 5),
        sourceContexts: subjectContexts
      };
    });

    return {
      hasCompared: true,
      sourceUniqueIds: sourceIds.length,
      ontologyFilesCount: (ontologyTexts || []).length,
      coveredIdsCount: sourceIds.length - missingIds.length,
      missingIdsCount: missingIds.length,
      missingOverflow: Math.max(0, missingIds.length - MAX_ONTOLOGY_MATCH_RESULTS),
      missing
    };
  };

  const canUpload = useMemo(() => !!selectedFile, [selectedFile]);

  const groupedRequirements = useMemo(() => {
    const normalizeVakgebiedFieldName = (value) =>
      String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const isVakgebiedAlias = (fieldName) => {
      const normalized = normalizeVakgebiedFieldName(fieldName);
      return normalized === 'vakgebied' || normalized === 'field of expertise';
    };

    const extractVakgebiedNumber = (label) => {
      const match = label.match(/^(\d+(?:[.,]\d+)?)\./);
      if (!match) return null;
      return Number(match[1].replace(',', '.'));
    };

    const groupsMap = new Map();

    requirementsData.forEach((requirement) => {
      const vakgebiedField = requirement.fields.find(
        (field) => field.typeLabel === 'AnnotationProperty' && isVakgebiedAlias(field.fieldName)
      );

      const vakgebiedValue = vakgebiedField?.values?.[0]?.trim() || 'Sin Vakgebied';

      if (!groupsMap.has(vakgebiedValue)) {
        groupsMap.set(vakgebiedValue, []);
      }

      groupsMap.get(vakgebiedValue).push(requirement);
    });

    const groups = Array.from(groupsMap.entries()).map(([name, requirements]) => ({
      name,
      requirements
    }));

    groups.sort((a, b) => {
      const aNum = extractVakgebiedNumber(a.name);
      const bNum = extractVakgebiedNumber(b.name);

      if (aNum !== null && bNum !== null) return aNum - bNum;
      if (aNum !== null) return -1;
      if (bNum !== null) return 1;

      return a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });
    });

    return groups;
  }, [requirementsData]);

  const objectTreeData = useMemo(() => {
    const rootNodes = CLASS_TREE_ROOTS.map((root) => ({
      id: root.id,
      label: root.defaultLabel,
      classes: [],
      objectsCount: 0
    }));

    const rootMap = {};
    const classMapByRoot = {};
    rootNodes.forEach((rootNode) => {
      rootMap[rootNode.id] = rootNode;
      classMapByRoot[rootNode.id] = new Map();
    });

    objectsData.forEach((objectItem) => {
      const rootIds = (objectItem.rootTypeIds || []).filter((rootId) => !!rootMap[rootId]);
      if (rootIds.length === 0) return;

      const groupingClass = objectItem.classParent
        || objectItem.classChild
        || objectItem.classTypes?.[0]
        || { id: '__SIN_CLASE__', label: 'Sin clase detectada' };

      rootIds.forEach((rootId) => {
        const classMap = classMapByRoot[rootId];
        const classId = groupingClass.id || '__SIN_CLASE__';

        if (!classMap.has(classId)) {
          classMap.set(classId, {
            id: classId,
            label: groupingClass.label || 'Sin clase detectada',
            objects: []
          });
        }

        classMap.get(classId).objects.push(objectItem);
      });
    });

    rootNodes.forEach((rootNode) => {
      const classes = Array.from(classMapByRoot[rootNode.id].values())
        .map((classNode) => ({
          ...classNode,
          objects: [...classNode.objects].sort((a, b) => {
            if (a.notation && b.notation) {
              return a.notation.localeCompare(b.notation, 'es', { numeric: true, sensitivity: 'base' });
            }
            if (a.notation) return -1;
            if (b.notation) return 1;
            return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
          })
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

      rootNode.classes = classes;
      rootNode.objectsCount = classes.reduce((acc, classNode) => acc + classNode.objects.length, 0);
    });

    return rootNodes;
  }, [objectsData]);

  const classTreeData = useMemo(() => {
    const rootIds = new Set();
    const rootsMap = {};
    CLASS_TREE_ROOTS.forEach((root) => {
      rootIds.add(root.id);
      rootsMap[root.id] = {
        id: root.id,
        label: root.defaultLabel,
        classes: []
      };
    });

    classesData.forEach((cls) => {
      const matchedParents = cls.subClassOf.filter((parent) => rootIds.has(parent.id));

      if (matchedParents.length === 0) return;

      matchedParents.forEach((parent) => {
        rootsMap[parent.id].label = parent.label || rootsMap[parent.id].label;
        rootsMap[parent.id].classes.push(cls);
      });
    });

    Object.values(rootsMap).forEach((root) => {
      root.classes.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    });

    return CLASS_TREE_ROOTS.map((root) => rootsMap[root.id]);
  }, [classesData]);

  const classInheritanceGraphData = useMemo(() => {
    if (classesData.length === 0) {
      return {
        nodes: [],
        nodeMap: {},
        edges: [],
        width: 980,
        height: 380,
        summary: {
          classes: 0,
          nodes: 0,
          edges: 0,
          levels: 0,
          externalParents: 0
        }
      };
    }

    const rootIdSet = new Set(CLASS_TREE_ROOTS.map((root) => root.id));
    const classIdSet = new Set(classesData.map((cls) => cls.id));
    const nodesById = {};
    const edges = [];
    const edgeSet = new Set();
    const classesWithoutParents = new Set();
    const orphanRootId = '__class_inheritance_orphan_root__';

    const mergeNodeType = (currentType, nextType) => {
      if (currentType === 'root' || nextType === 'root') return 'root';
      if (currentType === 'class' || nextType === 'class') return 'class';
      if (currentType === 'external' || nextType === 'external') return 'external';
      if (currentType === 'aux' || nextType === 'aux') return 'aux';
      return nextType || currentType || 'class';
    };

    const addNode = (id, label, nodeType = 'class') => {
      const normalizedId = normalizeId(id);
      if (!normalizedId) return '';

      if (!nodesById[normalizedId]) {
        nodesById[normalizedId] = {
          id: normalizedId,
          label: label || normalizedId,
          nodeType
        };
        return normalizedId;
      }

      const existingNode = nodesById[normalizedId];
      if (label && (!existingNode.label || existingNode.label === existingNode.id)) {
        existingNode.label = label;
      }
      existingNode.nodeType = mergeNodeType(existingNode.nodeType, nodeType);

      return normalizedId;
    };

    const addEdge = (fromId, toId) => {
      const from = normalizeId(fromId);
      const to = normalizeId(toId);
      if (!from || !to || from === to) return;

      const key = `${from}=>${to}`;
      if (edgeSet.has(key)) return;
      edgeSet.add(key);

      edges.push({
        from,
        to,
        type: 'inheritance'
      });
    };

    CLASS_TREE_ROOTS.forEach((root) => {
      addNode(root.id, root.defaultLabel, 'root');
    });

    classesData.forEach((cls) => {
      addNode(cls.id, cls.label || cls.id, 'class');
    });

    classesData.forEach((cls) => {
      const childId = normalizeId(cls.id);
      const rawParents = cls.subClassOf || [];

      if (rawParents.length === 0) {
        classesWithoutParents.add(childId);
        return;
      }

      let hasLinkedParent = false;

      rawParents.forEach((parent) => {
        const parentId = normalizeId(parent.id);
        if (!parentId || parentId === childId) return;

        let parentType = 'external';
        if (classIdSet.has(parentId)) {
          parentType = 'class';
        } else if (rootIdSet.has(parentId)) {
          parentType = 'root';
        }

        addNode(parentId, parent.label || parentId, parentType);
        addEdge(parentId, childId);
        hasLinkedParent = true;
      });

      if (!hasLinkedParent) {
        classesWithoutParents.add(childId);
      }
    });

    if (classesWithoutParents.size > 0) {
      addNode(orphanRootId, 'Sin subClassOf declarado', 'aux');
      classesWithoutParents.forEach((classId) => {
        addEdge(orphanRootId, classId);
      });
    }

    const childrenById = {};
    const indegreeById = {};
    Object.keys(nodesById).forEach((id) => {
      childrenById[id] = [];
      indegreeById[id] = 0;
    });

    edges.forEach((edge) => {
      if (!childrenById[edge.from] || indegreeById[edge.to] === undefined) return;
      childrenById[edge.from].push(edge.to);
      indegreeById[edge.to] += 1;
    });

    const nodeTypeWeight = {
      root: 0,
      aux: 1,
      external: 2,
      class: 3
    };

    const sortNodeIds = (ids) =>
      [...ids].sort((a, b) => {
        const nodeA = nodesById[a];
        const nodeB = nodesById[b];

        const byType = (nodeTypeWeight[nodeA?.nodeType] ?? 99) - (nodeTypeWeight[nodeB?.nodeType] ?? 99);
        if (byType !== 0) return byType;

        return (nodeA?.label || a).localeCompare(nodeB?.label || b, 'es', { sensitivity: 'base' });
      });

    const indegreeWork = { ...indegreeById };
    const levelById = {};
    const queue = sortNodeIds(Object.keys(nodesById).filter((id) => indegreeWork[id] === 0));

    queue.forEach((nodeId) => {
      levelById[nodeId] = 0;
    });

    while (queue.length > 0) {
      const currentId = queue.shift();
      const currentLevel = levelById[currentId] ?? 0;
      const sortedChildren = sortNodeIds(childrenById[currentId] || []);

      sortedChildren.forEach((childId) => {
        const nextLevel = currentLevel + 1;
        if ((levelById[childId] ?? -1) < nextLevel) {
          levelById[childId] = nextLevel;
        }

        indegreeWork[childId] -= 1;
        if (indegreeWork[childId] === 0) {
          queue.push(childId);
          const sortedQueue = sortNodeIds(queue);
          queue.splice(0, queue.length, ...sortedQueue);
        }
      });
    }

    const unresolvedIds = sortNodeIds(Object.keys(nodesById).filter((id) => levelById[id] === undefined));
    unresolvedIds.forEach((id) => {
      levelById[id] = 0;
    });

    const levelsMap = {};
    Object.values(nodesById).forEach((node) => {
      const level = Math.max(0, levelById[node.id] ?? 0);
      if (!levelsMap[level]) {
        levelsMap[level] = [];
      }
      levelsMap[level].push(node.id);
    });

    const sortedLevels = Object.keys(levelsMap)
      .map((level) => Number(level))
      .sort((a, b) => a - b);

    sortedLevels.forEach((level) => {
      levelsMap[level] = sortNodeIds(levelsMap[level]).map((id) => nodesById[id]);
    });

    const nodeWidth = 224;
    const nodeHeight = 54;
    const xPadding = 28;
    const xStep = 252;
    const yStart = 44;
    const yStep = 124;

    const maxNodesInLevel = Math.max(...sortedLevels.map((level) => levelsMap[level].length), 1);
    const maxLaneWidth = ((maxNodesInLevel - 1) * xStep) + nodeWidth;
    const svgWidth = Math.max(980, Math.round((xPadding * 2) + maxLaneWidth));

    const positionedNodes = [];
    const nodeMap = {};

    sortedLevels.forEach((level) => {
      const nodesAtLevel = levelsMap[level];
      const laneWidth = ((nodesAtLevel.length - 1) * xStep) + nodeWidth;
      const laneStartX = xPadding + Math.max(0, (maxLaneWidth - laneWidth) / 2);

      nodesAtLevel.forEach((node, index) => {
        const nextNode = {
          ...node,
          level,
          x: Math.round(laneStartX + (index * xStep)),
          y: yStart + (level * yStep),
          width: nodeWidth,
          height: nodeHeight
        };

        positionedNodes.push(nextNode);
        nodeMap[nextNode.id] = nextNode;
      });
    });

    const maxNodeBottom = positionedNodes.reduce((acc, node) => Math.max(acc, node.y + node.height), 0);
    const svgHeight = Math.max(380, Math.round(maxNodeBottom + 52));
    const externalParentsCount = Object.values(nodesById).filter((node) => node.nodeType === 'external').length;

    return {
      nodes: positionedNodes,
      nodeMap,
      edges,
      width: svgWidth,
      height: svgHeight,
      summary: {
        classes: classesData.length,
        nodes: positionedNodes.length,
        edges: edges.length,
        levels: sortedLevels.length,
        externalParents: externalParentsCount
      }
    };
  }, [classesData]);

  const generalDiagramData = useMemo(() => {
    const rootIds = new Set(CLASS_TREE_ROOTS.map((root) => root.id));
    const classMap = {};
    classesData.forEach((cls) => {
      classMap[cls.id] = cls;
    });

    const verificationPlans = verificationTreeData.flatMap((phaseNode) =>
      phaseNode.methods.flatMap((methodNode) =>
        methodNode.plans.map((plan) => ({
          ...plan,
          phaseLabel: phaseNode.label,
          methodLabel: methodNode.label
        }))
      )
    );

    const requirementLabels = {};
    requirementsData.forEach((req) => {
      requirementLabels[req.id] = req.name || req.notation || req.id;
    });

    const edges = [];
    const edgeSet = new Set();
    const addEdge = (from, to, type) => {
      if (!from || !to) return;
      const key = `${from}=>${to}::${type}`;
      if (edgeSet.has(key)) return;
      edgeSet.add(key);
      edges.push({ from, to, type });
    };

    const classNodes = [...classesData].sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    const classTreeRootLabels = {};
    classTreeData.forEach((rootNode) => {
      classTreeRootLabels[rootNode.id] = rootNode.label;
    });
    const rootNodes = CLASS_TREE_ROOTS.map((root) => ({
      id: root.id,
      label: classTreeRootLabels[root.id] || root.defaultLabel
    }));

    classNodes.forEach((cls) => {
      cls.subClassOf
        .filter((parent) => rootIds.has(parent.id))
        .forEach((parent) => addEdge(parent.id, cls.id, 'root-class'));
    });

    const normalizeLabelKey = (value) => (value || '').trim().toLowerCase();
    const instanceNodes = classInstancesData.map((instance) => {
      const hasMatchingClassLabel = (instance.classTypeIds || []).some((classId) => {
        const classLabel = classMap[classId]?.label || '';
        return normalizeLabelKey(classLabel) === normalizeLabelKey(instance.label);
      });

      const instanceCode = (instance.notation || '').trim();
      const displayLabel = hasMatchingClassLabel && instanceCode
        ? `${instanceCode} - ${instance.label}`
        : instance.label;

      return {
        ...instance,
        displayLabel
      };
    });

    instanceNodes.forEach((instance) => {
      instance.classTypeIds.forEach((classId) => {
        if (classMap[classId]) addEdge(classId, instance.id, 'class-instance');
      });
    });

    const planNodes = verificationPlans.map((plan) => ({
      id: plan.id,
      label: plan.methodLabel || 'VerificationPlan',
      phaseLabel: plan.phaseLabel || '-',
      subjectId: plan.hasAsSubject.id,
      requirementId: plan.isVerificationOf.id
    }));

    planNodes.forEach((plan) => {
      if (plan.subjectId) addEdge(plan.subjectId, plan.id, 'instance-plan');
      if (plan.requirementId) addEdge(plan.id, plan.requirementId, 'plan-requirement');
    });

    const referencedRequirementIds = new Set();
    classesData.forEach((cls) => {
      cls.requirements.forEach((req) => {
        referencedRequirementIds.add(req.id);
        addEdge(cls.id, req.id, 'class-requirement');
      });
    });
    planNodes.forEach((plan) => {
      if (plan.requirementId) referencedRequirementIds.add(plan.requirementId);
    });

    const requirementNodes = Array.from(referencedRequirementIds)
      .map((id) => ({
        id,
        label: requirementLabels[id] || id
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

    const lanes = [
      { key: 'roots', title: 'Raices', nodes: rootNodes.map((node) => ({ ...node, nodeType: 'root' })) },
      { key: 'classes', title: 'Clases', nodes: classNodes.map((cls) => ({ id: cls.id, label: cls.label, nodeType: 'class' })) },
      {
        key: 'instances',
        title: 'Objetos/Regiones',
        nodes: instanceNodes.map((item) => ({ id: item.id, label: item.displayLabel || item.label, nodeType: 'instance' }))
      },
      { key: 'plans', title: 'VerificationPlans', nodes: planNodes.map((plan) => ({ id: plan.id, label: plan.label, nodeType: 'plan' })) },
      { key: 'requirements', title: 'Requirements', nodes: requirementNodes.map((node) => ({ ...node, nodeType: 'requirement' })) }
    ];

    const nodeWidth = 196;
    const nodeHeight = 48;
    const xGap = 214;
    const yStart = 52;
    const yGap = 128;
    const xPadding = 24;
    const positionedNodes = [];
    const nodeMap = {};

    lanes.forEach((lane, laneIndex) => {
      lane.nodes.forEach((node, nodeIndex) => {
        const x = xPadding + (nodeIndex * xGap);
        const y = yStart + (laneIndex * yGap);
        const laneNode = {
          ...node,
          lane: lane.key,
          x,
          y,
          width: nodeWidth,
          height: nodeHeight
        };
        positionedNodes.push(laneNode);
        nodeMap[node.id] = laneNode;
      });
    });

    const maxLaneCount = Math.max(...lanes.map((lane) => lane.nodes.length), 1);
    const width = Math.max(980, xPadding * 2 + (maxLaneCount * xGap));
    const height = yStart + ((lanes.length - 1) * yGap) + nodeHeight + 40;

    const adjacentByNode = {};
    edges.forEach((edge) => {
      adjacentByNode[edge.from] = adjacentByNode[edge.from] || new Set();
      adjacentByNode[edge.to] = adjacentByNode[edge.to] || new Set();
      adjacentByNode[edge.from].add(edge.to);
      adjacentByNode[edge.to].add(edge.from);
    });

    const planMap = {};
    planNodes.forEach((plan) => {
      planMap[plan.id] = plan;
    });

    const instanceMapById = {};
    classInstancesData.forEach((instance) => {
      instanceMapById[instance.id] = instance;
    });

    const requirementMapById = {};
    requirementsData.forEach((req) => {
      requirementMapById[req.id] = req;
    });

    return {
      lanes,
      nodes: positionedNodes,
      nodeMap,
      edges,
      adjacentByNode,
      width,
      height,
      classMapById: classMap,
      instanceMapById,
      planMap,
      requirementMapById,
      summary: {
        roots: rootNodes.length,
        classes: classNodes.length,
        instances: instanceNodes.length,
        plans: planNodes.length,
        requirements: requirementNodes.length
      }
    };
  }, [classInstancesData, classTreeData, classesData, requirementsData, verificationTreeData]);

  const selectedGeneralNode = useMemo(() => {
    if (!selectedGeneralNodeId) return null;
    return generalDiagramData.nodeMap[selectedGeneralNodeId] || null;
  }, [selectedGeneralNodeId, generalDiagramData]);

  const objectMapById = useMemo(() => {
    const map = {};
    objectsData.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [objectsData]);

  const verificationPlanMapById = useMemo(() => {
    const map = {};
    verificationTreeData.forEach((phaseNode) => {
      phaseNode.methods.forEach((methodNode) => {
        methodNode.plans.forEach((planNode) => {
          map[planNode.id] = {
            ...planNode,
            phaseId: phaseNode.id,
            phaseLabel: phaseNode.label,
            methodId: methodNode.id,
            methodLabel: methodNode.label
          };
        });
      });
    });

    return map;
  }, [verificationTreeData]);

  const verificationPlanFilterCatalog = useMemo(() => {
    const phases = [];
    const methodsById = new Map();
    const plans = [];

    verificationTreeData.forEach((phaseNode) => {
      phases.push({ id: phaseNode.id, label: phaseNode.label });

      phaseNode.methods.forEach((methodNode) => {
        if (!methodsById.has(methodNode.id)) {
          methodsById.set(methodNode.id, { id: methodNode.id, label: methodNode.label });
        }

        methodNode.plans.forEach((planNode) => {
          plans.push({
            id: planNode.id,
            phaseId: phaseNode.id,
            phaseLabel: phaseNode.label,
            methodId: methodNode.id,
            methodLabel: methodNode.label
          });
        });
      });
    });

    phases.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    const methods = Array.from(methodsById.values())
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    plans.sort((a, b) => a.id.localeCompare(b.id, 'es', { sensitivity: 'base' }));

    return {
      phases,
      methods,
      plans
    };
  }, [verificationTreeData]);

  const availableMethodFilterOptions = useMemo(() => {
    if (!selectedPhaseFilterId) return verificationPlanFilterCatalog.methods;

    const methodsById = new Map();
    verificationPlanFilterCatalog.plans.forEach((plan) => {
      if (plan.phaseId !== selectedPhaseFilterId) return;
      if (!methodsById.has(plan.methodId)) {
        methodsById.set(plan.methodId, {
          id: plan.methodId,
          label: plan.methodLabel
        });
      }
    });

    return Array.from(methodsById.values())
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
  }, [selectedPhaseFilterId, verificationPlanFilterCatalog.methods, verificationPlanFilterCatalog.plans]);

  const hasActivePlanFilters = !!(selectedPhaseFilterId || selectedMethodFilterId);

  const filteredVerificationPlanIds = useMemo(() => {
    const ids = new Set();

    verificationPlanFilterCatalog.plans.forEach((plan) => {
      if (selectedPhaseFilterId && plan.phaseId !== selectedPhaseFilterId) return;
      if (selectedMethodFilterId && plan.methodId !== selectedMethodFilterId) return;
      ids.add(plan.id);
    });

    return ids;
  }, [verificationPlanFilterCatalog.plans, selectedPhaseFilterId, selectedMethodFilterId]);

  const selectedGeneralNodeDetails = useMemo(() => {
    if (!selectedGeneralNode) return null;

    const nodeId = selectedGeneralNode.id;
    const planGraphNode = generalDiagramData.planMap[nodeId];
    const planDetailNode = verificationPlanMapById[nodeId];
    if (planGraphNode || planDetailNode) {
      const subjectId = planDetailNode?.hasAsSubject?.id || planGraphNode?.subjectId || '';
      const requirementId = planDetailNode?.isVerificationOf?.id || planGraphNode?.requirementId || '';

      return {
        kind: 'plan',
        data: {
          ...(planGraphNode || {}),
          ...(planDetailNode || {}),
          subjectId,
          requirementId,
          subjectLabel: planDetailNode?.hasAsSubject?.label
            || generalDiagramData.nodeMap[subjectId]?.label
            || subjectId
            || '-',
          requirementLabel: planDetailNode?.isVerificationOf?.label
            || generalDiagramData.nodeMap[requirementId]?.label
            || requirementId
            || '-'
        }
      };
    }

    const objectNode = objectMapById[nodeId];
    if (objectNode) {
      return {
        kind: 'object',
        data: objectNode
      };
    }

    const classNode = generalDiagramData.classMapById[nodeId];
    if (classNode) {
      return {
        kind: 'class',
        data: classNode
      };
    }

    const requirementNode = generalDiagramData.requirementMapById[nodeId];
    if (requirementNode) {
      return {
        kind: 'requirement',
        data: requirementNode
      };
    }

    return {
      kind: 'basic',
      data: {
        id: selectedGeneralNode.id,
        label: selectedGeneralNode.label
      }
    };
  }, [selectedGeneralNode, objectMapById, verificationPlanMapById, generalDiagramData]);

  const generalDisplayedData = useMemo(() => {
    const nodeWidth = 196;
    const nodeHeight = 48;
    const xGap = 214;
    const yStart = 52;
    const yGap = 128;
    const xPadding = 24;

    const focusNodeId = generalObjectFocusMode
      ? (generalFocusAnchorNodeId || selectedGeneralNodeId)
      : selectedGeneralNodeId;
    const isFocused = !!focusNodeId && !!generalDiagramData.nodeMap[focusNodeId];
    const focusedNodeIds = new Set();
    const focusedClassIds = new Set();
    const focusedPlanIds = new Set();
    let lanes = [];

    if (generalObjectFocusMode && isFocused) {
      generalDiagramData.edges.forEach((edge) => {
        if (edge.type === 'class-instance' && edge.to === focusNodeId) {
          focusedClassIds.add(edge.from);
        }
      });

      generalDiagramData.edges.forEach((edge) => {
        if (edge.type !== 'instance-plan') return;
        if (focusedClassIds.has(edge.from) || edge.from === focusNodeId) {
          focusedPlanIds.add(edge.to);
        }
      });

      const focusedRootIds = new Set();
      const focusedRequirementIds = new Set();

      generalDiagramData.edges.forEach((edge) => {
        if (edge.type === 'root-class' && focusedClassIds.has(edge.to)) {
          focusedRootIds.add(edge.from);
        }
        if (edge.type === 'plan-requirement' && focusedPlanIds.has(edge.from)) {
          focusedRequirementIds.add(edge.to);
        }
      });

      const sortNodes = (a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
      const nodeById = generalDiagramData.nodeMap;

      const rootNodes = Array.from(focusedRootIds)
        .map((id) => nodeById[id])
        .filter(Boolean)
        .sort(sortNodes);

      const classNodes = Array.from(focusedClassIds)
        .map((id) => nodeById[id])
        .filter(Boolean)
        .sort(sortNodes);

      const objectNode = nodeById[focusNodeId] || null;
      const objectAndClassNodes = objectNode
        ? [objectNode, ...classNodes.filter((item) => item.id !== objectNode.id)]
        : classNodes;

      const requirementNodes = Array.from(focusedRequirementIds)
        .map((id) => nodeById[id])
        .filter(Boolean)
        .sort(sortNodes);

      const requirementOrderById = {};
      requirementNodes.forEach((node, index) => {
        requirementOrderById[node.id] = index;
      });

      const planRequirementOrderById = {};
      generalDiagramData.edges.forEach((edge) => {
        if (edge.type !== 'plan-requirement') return;
        if (!focusedPlanIds.has(edge.from) || !focusedRequirementIds.has(edge.to)) return;

        const requirementOrder = requirementOrderById[edge.to];
        if (typeof requirementOrder !== 'number') return;

        const currentOrder = planRequirementOrderById[edge.from];
        if (typeof currentOrder !== 'number' || requirementOrder < currentOrder) {
          planRequirementOrderById[edge.from] = requirementOrder;
        }
      });

      const planNodes = Array.from(focusedPlanIds)
        .map((id) => nodeById[id])
        .filter(Boolean)
        .sort((a, b) => {
          const aOrder = planRequirementOrderById[a.id];
          const bOrder = planRequirementOrderById[b.id];
          const hasA = typeof aOrder === 'number';
          const hasB = typeof bOrder === 'number';

          if (hasA && hasB && aOrder !== bOrder) {
            return aOrder - bOrder;
          }
          if (hasA && !hasB) return -1;
          if (!hasA && hasB) return 1;

          return sortNodes(a, b);
        });

      lanes = [
        {
          key: 'roots',
          title: 'N1 - Raiz',
          nodes: rootNodes.map((node) => ({ id: node.id, label: node.label, nodeType: node.nodeType || 'root' }))
        },
        {
          key: 'objectClass',
          title: 'N2 - Objeto y Clase',
          nodes: objectAndClassNodes.map((node) => ({ id: node.id, label: node.label, nodeType: node.nodeType }))
        },
        {
          key: 'plans',
          title: 'N3 - VerificationPlans',
          nodes: planNodes.map((node) => ({ id: node.id, label: node.label, nodeType: node.nodeType || 'plan' }))
        },
        {
          key: 'requirements',
          title: 'N4 - Requirements',
          nodes: requirementNodes.map((node) => ({ id: node.id, label: node.label, nodeType: node.nodeType || 'requirement' }))
        }
      ];
    } else {
      if (isFocused) {
        const queue = [{ id: focusNodeId, depth: 0 }];
        const visited = new Set([focusNodeId]);
        focusedNodeIds.add(focusNodeId);

        while (queue.length > 0) {
          const current = queue.shift();
          if (current.depth >= 2) continue;

          const neighbors = Array.from(generalDiagramData.adjacentByNode[current.id] || []);
          const maxNeighbors = current.depth === 0 ? 220 : 40;

          neighbors.slice(0, maxNeighbors).forEach((neighborId) => {
            focusedNodeIds.add(neighborId);
            if (visited.has(neighborId)) return;
            visited.add(neighborId);
            queue.push({ id: neighborId, depth: current.depth + 1 });
          });
        }

        const rootsLane = generalDiagramData.lanes.find((lane) => lane.key === 'roots');
        (rootsLane?.nodes || []).forEach((node) => focusedNodeIds.add(node.id));
      }

      lanes = generalDiagramData.lanes.map((lane) => {
        if (isFocused) {
          return {
            ...lane,
            nodes: lane.nodes.filter((node) => focusedNodeIds.has(node.id))
          };
        }

        if (showAllGeneralNodes) {
          return lane;
        }

        const laneLimit = GENERAL_FAST_LIMITS[lane.key] || lane.nodes.length;
        if (lane.nodes.length <= laneLimit) {
          return lane;
        }

        const overflowCount = lane.nodes.length - laneLimit;
        return {
          ...lane,
          nodes: [
            ...lane.nodes.slice(0, laneLimit),
            {
              id: `__overflow__${lane.key}`,
              label: `+${overflowCount} mas`,
              isOverflow: true
            }
          ]
        };
      });
    }

    const visibleNodeIds = new Set();
    lanes.forEach((lane) => {
      lane.nodes.forEach((node) => {
        if (!node.isOverflow) visibleNodeIds.add(node.id);
      });
    });

    let edges = [];
    if (generalObjectFocusMode && isFocused) {
      const focusEdges = [];
      const focusEdgeSet = new Set();
      const addFocusEdge = (from, to, type) => {
        if (!from || !to) return;
        const key = `${from}=>${to}::${type}`;
        if (focusEdgeSet.has(key)) return;
        focusEdgeSet.add(key);
        focusEdges.push({ from, to, type });
      };

      generalDiagramData.edges.forEach((edge) => {
        if (edge.type === 'root-class' && focusedClassIds.has(edge.to)) {
          addFocusEdge(edge.from, edge.to, 'root-class');
        }
        if (edge.type === 'plan-requirement' && focusedPlanIds.has(edge.from)) {
          addFocusEdge(edge.from, edge.to, 'plan-requirement');
        }
        if (edge.type === 'instance-plan' && edge.from === focusNodeId && focusedPlanIds.has(edge.to)) {
          addFocusEdge(edge.from, edge.to, 'instance-plan');
        }
      });

      focusedClassIds.forEach((classId) => {
        addFocusEdge(focusNodeId, classId, 'object-class');
      });

      generalDiagramData.edges.forEach((edge) => {
        if (
          edge.type === 'instance-plan'
          && focusedClassIds.has(edge.from)
          && focusedPlanIds.has(edge.to)
        ) {
          addFocusEdge(edge.from, edge.to, 'class-plan');
        }
      });

      edges = focusEdges.filter((edge) =>
        visibleNodeIds.has(edge.from) && visibleNodeIds.has(edge.to)
      );
    } else {
      edges = generalDiagramData.edges.filter((edge) => {
        if (!visibleNodeIds.has(edge.from) || !visibleNodeIds.has(edge.to)) return false;
        if (!generalObjectFocusMode || !selectedGeneralNodeId) return true;
        const anchorId = generalFocusAnchorNodeId || focusNodeId;
        if (!anchorId) return true;
        return edge.from === anchorId || edge.to === anchorId;
      });
    }

    const laneWidths = lanes.map((lane) => {
      let laneWidth = 0;
      lane.nodes.forEach((node, nodeIndex) => {
        const renderedWidth = node.isOverflow ? 170 : nodeWidth;
        laneWidth = Math.max(laneWidth, (nodeIndex * xGap) + renderedWidth);
      });
      return laneWidth;
    });

    const maxLaneWidth = Math.max(...laneWidths, nodeWidth);

    const nodes = [];
    const nodeMap = {};
    lanes.forEach((lane, laneIndex) => {
      const laneWidth = laneWidths[laneIndex] || 0;
      const laneOffsetX = laneWidth > 0 ? Math.max(0, (maxLaneWidth - laneWidth) / 2) : 0;

      lane.nodes.forEach((node, nodeIndex) => {
        const renderedWidth = node.isOverflow ? 170 : nodeWidth;
        const laneNode = {
          ...node,
          lane: lane.key,
          x: xPadding + Math.round(laneOffsetX) + (nodeIndex * xGap),
          y: yStart + (laneIndex * yGap),
          width: renderedWidth,
          height: nodeHeight
        };
        nodes.push(laneNode);
        nodeMap[laneNode.id] = laneNode;
      });
    });

    const alignNodesByParentEdges = (targetNodes, parentCentersById, relationType) => {
      if (!targetNodes || targetNodes.length <= 1) return;

      const positionedTargets = targetNodes
        .map((node) => {
          const parentCenters = edges
            .filter((edge) => edge.type === relationType && edge.to === node.id)
            .map((edge) => parentCentersById[edge.from])
            .filter((value) => typeof value === 'number');

          const targetCenter = parentCenters.length > 0
            ? parentCenters.reduce((sum, value) => sum + value, 0) / parentCenters.length
            : node.x + (node.width / 2);

          return {
            node,
            targetCenter,
            desiredX: targetCenter - (node.width / 2)
          };
        })
        .sort((a, b) => a.desiredX - b.desiredX);

      const minNodeGap = 14;
      let prevRight = xPadding - minNodeGap;

      positionedTargets.forEach((item) => {
        const nextX = Math.max(xPadding, item.desiredX, prevRight + minNodeGap);
        item.node.x = Math.round(nextX);
        prevRight = item.node.x + item.node.width;
      });

      const desiredCenter = positionedTargets.reduce((sum, item) => sum + item.targetCenter, 0) / positionedTargets.length;
      const minX = Math.min(...positionedTargets.map((item) => item.node.x));
      const maxRight = Math.max(...positionedTargets.map((item) => item.node.x + item.node.width));
      const currentCenter = (minX + maxRight) / 2;
      const minDelta = xPadding - minX;
      const appliedDelta = Math.max(minDelta, Math.round(desiredCenter - currentCenter));

      if (appliedDelta !== 0) {
        positionedTargets.forEach((item) => {
          item.node.x += appliedDelta;
        });
      }
    };

    const alignNodesByOutgoingEdges = (sourceNodes, targetCentersById, relationType) => {
      if (!sourceNodes || sourceNodes.length <= 1) return;

      const positionedSources = sourceNodes
        .map((node) => {
          const targetCenters = edges
            .filter((edge) => edge.type === relationType && edge.from === node.id)
            .map((edge) => targetCentersById[edge.to])
            .filter((value) => typeof value === 'number');

          const targetCenter = targetCenters.length > 0
            ? targetCenters.reduce((sum, value) => sum + value, 0) / targetCenters.length
            : node.x + (node.width / 2);

          return {
            node,
            targetCenter,
            desiredX: targetCenter - (node.width / 2)
          };
        })
        .sort((a, b) => a.desiredX - b.desiredX);

      const minNodeGap = 14;
      let prevRight = xPadding - minNodeGap;

      positionedSources.forEach((item) => {
        const nextX = Math.max(xPadding, item.desiredX, prevRight + minNodeGap);
        item.node.x = Math.round(nextX);
        prevRight = item.node.x + item.node.width;
      });

      const desiredCenter = positionedSources.reduce((sum, item) => sum + item.targetCenter, 0) / positionedSources.length;
      const minX = Math.min(...positionedSources.map((item) => item.node.x));
      const maxRight = Math.max(...positionedSources.map((item) => item.node.x + item.node.width));
      const currentCenter = (minX + maxRight) / 2;
      const minDelta = xPadding - minX;
      const appliedDelta = Math.max(minDelta, Math.round(desiredCenter - currentCenter));

      if (appliedDelta !== 0) {
        positionedSources.forEach((item) => {
          item.node.x += appliedDelta;
        });
      }
    };

    if (generalObjectFocusMode && isFocused) {
      const requirementLaneNodes = nodes.filter((node) => node.lane === 'requirements' && !node.isOverflow);
      const requirementCentersById = {};
      requirementLaneNodes.forEach((node) => {
        requirementCentersById[node.id] = node.x + (node.width / 2);
      });

      const planLaneNodes = nodes.filter((node) => node.lane === 'plans' && !node.isOverflow);
      alignNodesByOutgoingEdges(planLaneNodes, requirementCentersById, 'plan-requirement');

      const planCentersById = {};
      planLaneNodes.forEach((node) => {
        planCentersById[node.id] = node.x + (node.width / 2);
      });

      alignNodesByParentEdges(requirementLaneNodes, planCentersById, 'plan-requirement');

      const updatedRequirementCentersById = {};
      requirementLaneNodes.forEach((node) => {
        updatedRequirementCentersById[node.id] = node.x + (node.width / 2);
      });
      alignNodesByOutgoingEdges(planLaneNodes, updatedRequirementCentersById, 'plan-requirement');
    }

    const width = Math.max(980, Math.ceil(xPadding * 2 + maxLaneWidth));
    const height = yStart + ((lanes.length - 1) * yGap) + nodeHeight + 40;

    const isTrimmed = !isFocused && !showAllGeneralNodes && lanes.some((lane) => lane.nodes.some((node) => node.isOverflow));

    return {
      lanes,
      nodes,
      nodeMap,
      edges,
      width,
      height,
      isTrimmed,
      isFocused
    };
  }, [generalDiagramData, generalFocusAnchorNodeId, generalObjectFocusMode, selectedGeneralNodeId, showAllGeneralNodes]);

  const selectedGeneralConnections = useMemo(() => {
    if (!selectedGeneralNodeId) {
      return {
        outgoing: [],
        incoming: [],
        outgoingOverflow: 0,
        incomingOverflow: 0
      };
    }

    const allOutgoing = generalDisplayedData.edges
      .filter((edge) => edge.from === selectedGeneralNodeId)
      .map((edge) => ({
        id: `${edge.from}=>${edge.to}:${edge.type}`,
        edgeType: edge.type,
        edgeLabel: EDGE_TYPE_LABELS[edge.type] || edge.type,
        nodeId: edge.to,
        nodeLabel: generalDisplayedData.nodeMap[edge.to]?.label || edge.to
      }))
      .sort((a, b) => a.nodeLabel.localeCompare(b.nodeLabel, 'es', { sensitivity: 'base' }));

    const allIncoming = generalDisplayedData.edges
      .filter((edge) => edge.to === selectedGeneralNodeId)
      .map((edge) => ({
        id: `${edge.from}=>${edge.to}:${edge.type}`,
        edgeType: edge.type,
        edgeLabel: EDGE_TYPE_LABELS[edge.type] || edge.type,
        nodeId: edge.from,
        nodeLabel: generalDisplayedData.nodeMap[edge.from]?.label || edge.from
      }))
      .sort((a, b) => a.nodeLabel.localeCompare(b.nodeLabel, 'es', { sensitivity: 'base' }));

    return {
      outgoing: allOutgoing.slice(0, MAX_NODE_CONNECTIONS_IN_PANEL),
      incoming: allIncoming.slice(0, MAX_NODE_CONNECTIONS_IN_PANEL),
      outgoingOverflow: Math.max(0, allOutgoing.length - MAX_NODE_CONNECTIONS_IN_PANEL),
      incomingOverflow: Math.max(0, allIncoming.length - MAX_NODE_CONNECTIONS_IN_PANEL)
    };
  }, [selectedGeneralNodeId, generalDisplayedData]);

  const buildVakgebiedExpandedState = (groups, value) =>
    groups.reduce((acc, group) => {
      acc[group.name] = value;
      return acc;
    }, {});

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    setIsPrepared(false);
    setVerificationTreeData([]);
    setVerificationSummary({ plans: 0, phases: 0, methods: 0 });
    setRequirementsData([]);
    setRequirementsSummary({ requirements: 0, annotationFields: 0, controlFields: 0 });
    setOntologiesData([]);
    setOntologiesSummary({ ontologies: 0, withDate: 0, withVersionInfo: 0, withPrefLabel: 0 });
    setSourceTtlText('');
    setSelectedOntologyFile(null);
    setSelectedOntologyMissingFiles([]);
    setIsOntologyMatchProcessing(false);
    setOntologyMatchState(createOntologyMatchState());
    setIsOntologyMissingProcessing(false);
    setOntologyMissingState(createOntologyMissingState());
    setClassesData([]);
    setClassesSummary({ classes: 0, inheritanceLinks: 0, roots: 0 });
    setClassInstancesData([]);
    setObjectsData([]);
    setObjectsSummary({ objects: 0, realObjects: 0, spatialRegions: 0, withClassAndSubclass: 0 });
    setIsObjectGraphDialogOpen(false);
    setIsClassInheritanceGraphDialogOpen(false);
    setSelectedGeneralNodeId('');
    setGeneralFocusAnchorNodeId('');
    setGeneralObjectFocusMode(false);
    setShowAllGeneralNodes(false);
    setExpandedClassRoots({
      'nen2660:RealObject': true,
      'nen2660:SpatialRegion': true
    });
    setExpandedClassCards({});
    setExpandedObjectCards({});
    setExpandedObjectRoots({
      'nen2660:RealObject': true,
      'nen2660:SpatialRegion': true
    });
    setExpandedObjectClasses({});
    setExpandedRequirements({});
    setExpandedVakgebiedGroups({});
    setExpandedPhases({});
    setExpandedMethods({});
    setFreeNodePositions({});
    setDraggingNodeId('');
    setSelectedPhaseFilterId('');
    setSelectedMethodFilterId('');
    graphPanRef.current = createGraphPanState();
    graphNodeDragRef.current = createGraphNodeDragState();
    graphSuppressClickRef.current = false;
    setIsGraphDragging(false);

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const hasTTLExtension = file.name.toLowerCase().endsWith('.ttl');
    if (!hasTTLExtension) {
      setSelectedFile(null);
      showError('Solo se permiten archivos con extension .ttl');
      return;
    }

    setSelectedFile(file);
  };

  const handlePrepareUpload = () => {
    if (!selectedFile) {
      showError('Selecciona un archivo TTL antes de cargarlo');
      return;
    }

    const processFile = async () => {
      setIsProcessing(true);
      try {
        const ttlText = await selectedFile.text();
        const labelMap = extractLabelMap(ttlText);
        const propertyDefinitions = extractPropertyDefinitions(ttlText);
        const documentMap = extractDocumentMap(ttlText);

        const parsedVerification = parseVerificationPlans(ttlText, labelMap);
        const parsedRequirements = parseRequirements(ttlText, propertyDefinitions, documentMap);
        const parsedOntologies = parseOntologies(ttlText, labelMap);
        const parsedClasses = parseClasses(ttlText, labelMap, propertyDefinitions);
        const parsedObjects = parseObjects(ttlText, parsedClasses.classes, labelMap, propertyDefinitions);

        setVerificationTreeData(parsedVerification.phases);
        setVerificationSummary(parsedVerification.summary);
        setExpandedPhases(buildExpandedPhasesState(parsedVerification.phases, true));
        setExpandedMethods(buildExpandedMethodsState(parsedVerification.phases, false));

        setRequirementsData(parsedRequirements.requirements);
        setRequirementsSummary(parsedRequirements.summary);
        setOntologiesData(parsedOntologies.ontologies);
        setOntologiesSummary(parsedOntologies.summary);
        setSourceTtlText(ttlText);
        setOntologyMatchState(createOntologyMatchState());
        setOntologyMissingState(createOntologyMissingState());
        setClassesData(parsedClasses.classes);
        setClassesSummary(parsedClasses.summary);
        setClassInstancesData(parsedObjects.classInstances);
        setObjectsData(parsedObjects.objects);
        setObjectsSummary(parsedObjects.summary);
        setIsObjectGraphDialogOpen(false);
        setIsClassInheritanceGraphDialogOpen(false);
        setSelectedGeneralNodeId('');
        setGeneralFocusAnchorNodeId('');
        setGeneralObjectFocusMode(false);
        setShowAllGeneralNodes(false);
        setExpandedClassRoots({
          'nen2660:RealObject': true,
          'nen2660:SpatialRegion': true
        });
        setExpandedClassCards({});
        setExpandedObjectCards({});
        setExpandedObjectRoots({
          'nen2660:RealObject': true,
          'nen2660:SpatialRegion': true
        });
        setExpandedObjectClasses({});
        setExpandedRequirements({});
        setExpandedVakgebiedGroups({});
        setFreeNodePositions({});
        setDraggingNodeId('');
        setSelectedPhaseFilterId('');
        setSelectedMethodFilterId('');
        graphPanRef.current = createGraphPanState();
        graphNodeDragRef.current = createGraphNodeDragState();
        graphSuppressClickRef.current = false;
        setIsGraphDragging(false);

        setIsPrepared(true);
        showSuccess(`Archivo ${selectedFile.name} procesado para vistas semanticas`);
      } catch (error) {
        setVerificationTreeData([]);
        setVerificationSummary({ plans: 0, phases: 0, methods: 0 });
        setRequirementsData([]);
        setRequirementsSummary({ requirements: 0, annotationFields: 0, controlFields: 0 });
        setOntologiesData([]);
        setOntologiesSummary({ ontologies: 0, withDate: 0, withVersionInfo: 0, withPrefLabel: 0 });
        setSourceTtlText('');
        setIsOntologyMatchProcessing(false);
        setOntologyMatchState(createOntologyMatchState());
        setIsOntologyMissingProcessing(false);
        setOntologyMissingState(createOntologyMissingState());
        setClassesData([]);
        setClassesSummary({ classes: 0, inheritanceLinks: 0, roots: 0 });
        setClassInstancesData([]);
        setObjectsData([]);
        setObjectsSummary({ objects: 0, realObjects: 0, spatialRegions: 0, withClassAndSubclass: 0 });
        setIsObjectGraphDialogOpen(false);
        setIsClassInheritanceGraphDialogOpen(false);
        setSelectedGeneralNodeId('');
        setGeneralFocusAnchorNodeId('');
        setGeneralObjectFocusMode(false);
        setShowAllGeneralNodes(false);
        setExpandedClassRoots({
          'nen2660:RealObject': true,
          'nen2660:SpatialRegion': true
        });
        setExpandedClassCards({});
        setExpandedObjectCards({});
        setExpandedObjectRoots({
          'nen2660:RealObject': true,
          'nen2660:SpatialRegion': true
        });
        setExpandedObjectClasses({});
        setExpandedRequirements({});
        setExpandedVakgebiedGroups({});
        setExpandedPhases({});
        setExpandedMethods({});
        setFreeNodePositions({});
        setDraggingNodeId('');
        setSelectedPhaseFilterId('');
        setSelectedMethodFilterId('');
        graphPanRef.current = createGraphPanState();
        graphNodeDragRef.current = createGraphNodeDragState();
        graphSuppressClickRef.current = false;
        setIsGraphDragging(false);
        setIsPrepared(false);
        showError('No se pudo procesar el archivo TTL');
      } finally {
        setIsProcessing(false);
      }
    };

    processFile();
  };

  const handleOntologyFileSelect = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setSelectedOntologyFile(null);
      setOntologyMatchState(createOntologyMatchState());
      return;
    }

    const hasTTLExtension = file.name.toLowerCase().endsWith('.ttl');
    if (!hasTTLExtension) {
      setSelectedOntologyFile(null);
      setOntologyMatchState(createOntologyMatchState());
      showError('El archivo de ontologia debe tener extension .ttl');
      return;
    }

    setSelectedOntologyFile(file);
    setOntologyMatchState(createOntologyMatchState());
  };

  const handleRunOntologyIdMatch = () => {
    if (!sourceTtlText) {
      showError('Primero carga y procesa el archivo TTL principal');
      return;
    }

    if (!selectedOntologyFile) {
      showError('Selecciona el archivo TTL de ontologia');
      return;
    }

    const compareIds = async () => {
      setIsOntologyMatchProcessing(true);
      try {
        const ontologyText = await selectedOntologyFile.text();
        const nextState = buildOntologyMatchResults(sourceTtlText, ontologyText);
        setOntologyMatchState(nextState);

        showSuccess(`Cruce de IDs completado: ${nextState.sharedIdsCount} coincidencias`);
      } catch (error) {
        setOntologyMatchState(createOntologyMatchState());
        showError('No se pudo leer el archivo TTL de ontologia');
      } finally {
        setIsOntologyMatchProcessing(false);
      }
    };

    compareIds();
  };

  const handleOntologyMissingFilesSelect = (event) => {
    const fileList = Array.from(event.target.files || []);
    if (fileList.length === 0) return;

    const invalidFiles = fileList.filter((file) => !file.name.toLowerCase().endsWith('.ttl'));
    if (invalidFiles.length > 0) {
      showError('Todos los archivos de ontologia deben tener extension .ttl');
    }

    const ttlFiles = fileList.filter((file) => file.name.toLowerCase().endsWith('.ttl'));
    if (ttlFiles.length === 0) {
      event.target.value = '';
      return;
    }

    setSelectedOntologyMissingFiles((prev) => {
      const byKey = new Map(prev.map((file) => [`${file.name}::${file.size}::${file.lastModified}`, file]));
      ttlFiles.forEach((file) => {
        const key = `${file.name}::${file.size}::${file.lastModified}`;
        if (!byKey.has(key)) {
          byKey.set(key, file);
        }
      });
      return Array.from(byKey.values());
    });

    setOntologyMissingState(createOntologyMissingState());
    event.target.value = '';
  };

  const handleRemoveOntologyMissingFile = (targetFile) => {
    setSelectedOntologyMissingFiles((prev) =>
      prev.filter((file) => !(
        file.name === targetFile.name
        && file.size === targetFile.size
        && file.lastModified === targetFile.lastModified
      ))
    );
    setOntologyMissingState(createOntologyMissingState());
  };

  const handleClearOntologyMissingFiles = () => {
    setSelectedOntologyMissingFiles([]);
    setOntologyMissingState(createOntologyMissingState());
  };

  const handleRunOntologyMissingCheck = () => {
    if (!sourceTtlText) {
      showError('Primero carga y procesa el archivo TTL principal');
      return;
    }

    if (selectedOntologyMissingFiles.length === 0) {
      showError('Añade al menos un TTL de ontologia');
      return;
    }

    const compareMissingIds = async () => {
      setIsOntologyMissingProcessing(true);
      try {
        const ontologyTexts = await Promise.all(selectedOntologyMissingFiles.map((file) => file.text()));
        const nextState = buildOntologyMissingResults(sourceTtlText, ontologyTexts);
        setOntologyMissingState(nextState);
        showSuccess(`IDs faltantes calculados: ${nextState.missingIdsCount}`);
      } catch (error) {
        setOntologyMissingState(createOntologyMissingState());
        showError('No se pudieron leer los archivos TTL de ontologia');
      } finally {
        setIsOntologyMissingProcessing(false);
      }
    };

    compareMissingIds();
  };

  const togglePhase = (phaseId) => {
    setExpandedPhases((prev) => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };

  const toggleMethod = (phaseId, methodId) => {
    const key = getMethodKey(phaseId, methodId);
    setExpandedMethods((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleExpandAll = () => {
    setExpandedPhases(buildExpandedPhasesState(verificationTreeData, true));
    setExpandedMethods(buildExpandedMethodsState(verificationTreeData, true));
  };

  const handleCollapseAll = () => {
    setExpandedPhases(buildExpandedPhasesState(verificationTreeData, false));
    setExpandedMethods(buildExpandedMethodsState(verificationTreeData, false));
  };

  const handleRequirementToggle = (requirementId) => {
    setExpandedRequirements((prev) => ({ ...prev, [requirementId]: !prev[requirementId] }));
  };

  const handleVakgebiedToggle = (groupName) => {
    setExpandedVakgebiedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const handleExpandAllVakgebied = () => {
    setExpandedVakgebiedGroups(buildVakgebiedExpandedState(groupedRequirements, true));
  };

  const handleCollapseAllVakgebied = () => {
    setExpandedVakgebiedGroups(buildVakgebiedExpandedState(groupedRequirements, false));
    setExpandedRequirements({});
  };

  const handleClassRootToggle = (rootId) => {
    setExpandedClassRoots((prev) => ({
      ...prev,
      [rootId]: !prev[rootId]
    }));
  };

  const handleClassCardToggle = (classId) => {
    setExpandedClassCards((prev) => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  const handleExpandAllClassDetails = () => {
    const next = {};
    classesData.forEach((cls) => {
      next[cls.id] = true;
    });
    setExpandedClassCards(next);
  };

  const handleCollapseAllClassDetails = () => {
    setExpandedClassCards({});
  };

  const handleObjectCardToggle = (objectId) => {
    setExpandedObjectCards((prev) => ({
      ...prev,
      [objectId]: !prev[objectId]
    }));
  };

  const handleObjectRootToggle = (rootId) => {
    setExpandedObjectRoots((prev) => ({
      ...prev,
      [rootId]: !prev[rootId]
    }));
  };

  const handleObjectClassToggle = (rootId, classId) => {
    const key = getObjectClassKey(rootId, classId);
    setExpandedObjectClasses((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleExpandAllObjects = () => {
    const nextObjectCards = {};
    const nextObjectClasses = {};

    objectTreeData.forEach((rootNode) => {
      rootNode.classes.forEach((classNode) => {
        nextObjectClasses[getObjectClassKey(rootNode.id, classNode.id)] = true;
        classNode.objects.forEach((item) => {
          nextObjectCards[item.id] = true;
        });
      });
    });

    setExpandedObjectRoots({
      'nen2660:RealObject': true,
      'nen2660:SpatialRegion': true
    });
    setExpandedObjectClasses(nextObjectClasses);
    setExpandedObjectCards(nextObjectCards);
  };

  const handleCollapseAllObjects = () => {
    setExpandedObjectRoots({
      'nen2660:RealObject': false,
      'nen2660:SpatialRegion': false
    });
    setExpandedObjectClasses({});
    setExpandedObjectCards({});
  };

  const handleGeneralNodeSelect = (nodeId) => {
    setSelectedGeneralNodeId(nodeId);
  };

  const handleGraphNodePositionsReset = () => {
    setFreeNodePositions({});
    setDraggingNodeId('');
    graphNodeDragRef.current = createGraphNodeDragState();
    graphSuppressClickRef.current = false;
  };

  const handleGraphNodeMouseDown = (event, node) => {
    if (event.button !== 0) return;
    event.stopPropagation();

    const initialPosition = freeNodePositions[node.id] || { x: node.x, y: node.y };
    graphNodeDragRef.current = {
      isActive: true,
      nodeId: node.id,
      startX: event.clientX,
      startY: event.clientY,
      originX: initialPosition.x,
      originY: initialPosition.y,
      moved: false
    };

    setDraggingNodeId(node.id);
    setIsGraphDragging(false);
  };

  const handleGraphMouseDown = (event) => {
    if (event.button !== 0) return;
    const container = graphContainerRef.current;
    if (!container) return;

    graphPanRef.current = {
      isActive: true,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      moved: false
    };

    setIsGraphDragging(true);
  };

  const handleGraphMouseMove = (event) => {
    const nodeDragState = graphNodeDragRef.current;
    if (nodeDragState.isActive) {
      const rawDx = event.clientX - nodeDragState.startX;
      const rawDy = event.clientY - nodeDragState.startY;
      const hasExceededDragThreshold = Math.abs(rawDx) >= GRAPH_DRAG_THRESHOLD_PX || Math.abs(rawDy) >= GRAPH_DRAG_THRESHOLD_PX;

      if (!nodeDragState.moved && !hasExceededDragThreshold) {
        return;
      }

      if (!nodeDragState.moved && hasExceededDragThreshold) {
        graphNodeDragRef.current = {
          ...nodeDragState,
          moved: true
        };
        graphSuppressClickRef.current = true;
        setIsGraphDragging(true);
      }

      const dx = rawDx;
      const dy = rawDy;
      const nextX = Math.max(8, nodeDragState.originX + dx);
      const nextY = Math.max(12, nodeDragState.originY + dy);

      setFreeNodePositions((prev) => {
        const currentNode = prev[nodeDragState.nodeId];
        if (currentNode && Math.abs(currentNode.x - nextX) < 0.2 && Math.abs(currentNode.y - nextY) < 0.2) {
          return prev;
        }

        return {
          ...prev,
          [nodeDragState.nodeId]: { x: nextX, y: nextY }
        };
      });

      return;
    }

    const container = graphContainerRef.current;
    const panState = graphPanRef.current;
    if (!container || !panState.isActive) return;

    const dx = event.clientX - panState.startX;
    const dy = event.clientY - panState.startY;

    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) {
      graphPanRef.current.moved = true;
    }

    container.scrollLeft = panState.scrollLeft - dx;
    container.scrollTop = panState.scrollTop - dy;
  };

  const handleGraphMouseUpOrLeave = () => {
    const wasNodeDragging = graphNodeDragRef.current.isActive;
    const nodeWasMoved = graphNodeDragRef.current.moved;
    const wasPanning = graphPanRef.current.isActive;
    if (!wasNodeDragging && !wasPanning) return;

    if (wasNodeDragging) {
      if (nodeWasMoved) {
        graphSuppressClickRef.current = true;
      }
      graphNodeDragRef.current = createGraphNodeDragState();
      setDraggingNodeId('');
    }

    if (wasPanning && graphPanRef.current.moved) {
      graphSuppressClickRef.current = true;
    }

    graphPanRef.current = createGraphPanState();
    setIsGraphDragging(false);
  };

  const handleGeneralGraphNodeClick = (nodeId) => {
    if (graphSuppressClickRef.current) {
      graphSuppressClickRef.current = false;
      return;
    }

    handleGeneralNodeSelect(nodeId);
  };

  const centerGraphViewport = (attempt = 0) => {
    const container = graphContainerRef.current;
    if (!container || container.clientWidth === 0 || container.clientHeight === 0) {
      if (attempt < 12) {
        requestAnimationFrame(() => centerGraphViewport(attempt + 1));
      }
      return;
    }

    const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    container.scrollLeft = Math.round(maxLeft / 2);
    container.scrollTop = Math.round(maxTop / 2);
  };

  const resetGeneralGraphFilters = () => {
    setSelectedPhaseFilterId('');
    setSelectedMethodFilterId('');
  };

  const handleViewObjectGraph = (objectId = '') => {
    resetGeneralGraphFilters();
    const normalizedObjectId = normalizeId(objectId);
    setSelectedGeneralNodeId(normalizedObjectId);
    setGeneralFocusAnchorNodeId(normalizedObjectId);
    setGeneralObjectFocusMode(!!normalizedObjectId);
    setShowAllGeneralNodes(false);
    setIsObjectGraphDialogOpen(true);
    requestAnimationFrame(() => centerGraphViewport());
  };

  const handleCloseObjectGraphDialog = () => {
    setIsObjectGraphDialogOpen(false);
    setSelectedGeneralNodeId('');
    setGeneralFocusAnchorNodeId('');
    setGeneralObjectFocusMode(false);
  };

  const handleOpenClassInheritanceGraphDialog = () => {
    if (classInheritanceGraphData.summary.classes === 0) return;
    setIsClassInheritanceGraphDialogOpen(true);
  };

  const handleCloseClassInheritanceGraphDialog = () => {
    setIsClassInheritanceGraphDialogOpen(false);
  };

  const handleDisableObjectGraphFocus = () => {
    setGeneralObjectFocusMode(false);
    setGeneralFocusAnchorNodeId('');
  };

  const handleClearGeneralSelection = () => {
    setSelectedGeneralNodeId('');
    setGeneralFocusAnchorNodeId('');
    setGeneralObjectFocusMode(false);
  };

  const handleCloseGeneralNodePanel = () => {
    setSelectedGeneralNodeId('');
  };

  const renderGeneralGraphCanvas = (enableSelection = true, options = {}) => {
    const viewportHeight = options.viewportHeight || '72vh';
    const showControls = options.showControls !== false;
    const resolveGraphNodeType = (node) => node.nodeType
      || (node.lane === 'plans'
        ? 'plan'
        : node.lane === 'requirements'
          ? 'requirement'
          : '');

    const baseRenderedNodes = generalDisplayedData.nodes.map((node) => {
      const customPosition = freeNodePositions[node.id];
      if (!customPosition) return node;
      return {
        ...node,
        x: customPosition.x,
        y: customPosition.y
      };
    });

    let renderedNodes = baseRenderedNodes;
    let renderedEdges = generalDisplayedData.edges;

    if (hasActivePlanFilters) {
      const allPlanIds = new Set(
        baseRenderedNodes
          .filter((node) => resolveGraphNodeType(node) === 'plan')
          .map((node) => node.id)
      );
      const excludedPlanIds = new Set();
      allPlanIds.forEach((planId) => {
        if (!filteredVerificationPlanIds.has(planId)) {
          excludedPlanIds.add(planId);
        }
      });

      const requirementIdsFromIncludedPlans = new Set();
      const requirementIdsFromExcludedPlans = new Set();

      generalDisplayedData.edges.forEach((edge) => {
        if (edge.type !== 'plan-requirement') return;
        if (filteredVerificationPlanIds.has(edge.from)) {
          requirementIdsFromIncludedPlans.add(edge.to);
        }
        if (excludedPlanIds.has(edge.from)) {
          requirementIdsFromExcludedPlans.add(edge.to);
        }
      });

      const requirementIdsToHide = new Set();
      requirementIdsFromExcludedPlans.forEach((reqId) => {
        if (!requirementIdsFromIncludedPlans.has(reqId)) {
          requirementIdsToHide.add(reqId);
        }
      });

      renderedNodes = baseRenderedNodes.filter((node) => {
        const nodeType = resolveGraphNodeType(node);

        if (nodeType === 'plan' && !filteredVerificationPlanIds.has(node.id)) return false;
        if (nodeType === 'requirement' && requirementIdsToHide.has(node.id)) return false;
        return true;
      });

      renderedEdges = generalDisplayedData.edges.filter((edge) => {
        if (excludedPlanIds.has(edge.from) || excludedPlanIds.has(edge.to)) return false;
        if (requirementIdsToHide.has(edge.from) || requirementIdsToHide.has(edge.to)) return false;
        return true;
      });

      const compactXPadding = 24;
      const compactXGap = 214;
      const compactedXByNodeId = {};
      const laneNodesMap = new Map();
      let maxLaneWidth = 0;

      renderedNodes.forEach((node) => {
        const laneKey = node.lane || 'other';
        if (!laneNodesMap.has(laneKey)) {
          laneNodesMap.set(laneKey, []);
        }
        laneNodesMap.get(laneKey).push(node);
      });

      laneNodesMap.forEach((laneNodes) => {
        const sortedLaneNodes = [...laneNodes]
          .sort((a, b) => (a.x - b.x) || a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

        if (sortedLaneNodes.length === 0) return;

        const laneWidth = ((sortedLaneNodes.length - 1) * compactXGap) + sortedLaneNodes[0].width;
        maxLaneWidth = Math.max(maxLaneWidth, laneWidth);
      });

      laneNodesMap.forEach((laneNodes) => {
        const sortedLaneNodes = [...laneNodes]
          .sort((a, b) => (a.x - b.x) || a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

        if (sortedLaneNodes.length === 0) return;

        const laneWidth = ((sortedLaneNodes.length - 1) * compactXGap) + sortedLaneNodes[0].width;
        const laneStartX = compactXPadding + Math.max(0, (maxLaneWidth - laneWidth) / 2);

        sortedLaneNodes.forEach((node, index) => {
          compactedXByNodeId[node.id] = laneStartX + (index * compactXGap);
        });
      });

      renderedNodes = renderedNodes.map((node) => ({
        ...node,
        x: compactedXByNodeId[node.id] ?? node.x
      }));
    }

    const calculateNodeBounds = (nodes) => {
      if (nodes.length === 0) {
        return {
          minNodeX: 0,
          maxNodeRight: 0,
          maxNodeBottom: 0
        };
      }

      return nodes.reduce((acc, node) => ({
        minNodeX: Math.min(acc.minNodeX, node.x),
        maxNodeRight: Math.max(acc.maxNodeRight, node.x + node.width),
        maxNodeBottom: Math.max(acc.maxNodeBottom, node.y + node.height)
      }), {
        minNodeX: Number.POSITIVE_INFINITY,
        maxNodeRight: 0,
        maxNodeBottom: 0
      });
    };

    let { minNodeX, maxNodeRight, maxNodeBottom } = calculateNodeBounds(renderedNodes);
    const baseContentWidth = Math.max(hasActivePlanFilters ? 0 : generalDisplayedData.width, maxNodeRight + 24);
    const svgWidth = Math.max(980, Math.round(baseContentWidth));

    if (hasActivePlanFilters && renderedNodes.length > 0) {
      const graphWidth = Math.max(0, maxNodeRight - minNodeX);
      const centeredLeft = Math.max(24, Math.round((svgWidth - graphWidth) / 2));
      const offsetX = centeredLeft - minNodeX;

      if (offsetX !== 0) {
        renderedNodes = renderedNodes.map((node) => ({
          ...node,
          x: node.x + offsetX
        }));

        ({ minNodeX, maxNodeRight, maxNodeBottom } = calculateNodeBounds(renderedNodes));
      }
    }

    const nodeRenderMap = {};
    renderedNodes.forEach((node) => {
      nodeRenderMap[node.id] = node;
    });

    const contentWidth = Math.max(svgWidth, maxNodeRight + 24);
    const contentHeight = Math.max(hasActivePlanFilters ? 0 : generalDisplayedData.height, maxNodeBottom + 24);
    const svgHeight = Math.max(320, Math.round(contentHeight));

    return (
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#f8fafc' }}>
        {showControls && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              px: 1,
              py: 0.75,
              borderBottom: '1px solid #e2e8f0',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Arrastra nodos para reubicarlos o arrastra el fondo para mover el grafo.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 185 }}>
                <Select
                  displayEmpty
                  value={selectedPhaseFilterId}
                  onChange={(event) => {
                    const nextPhaseFilterId = event.target.value;
                    setSelectedPhaseFilterId(nextPhaseFilterId);

                    if (selectedMethodFilterId) {
                      const methodStillAvailable = verificationPlanFilterCatalog.plans.some((plan) => {
                        if (!nextPhaseFilterId) return plan.methodId === selectedMethodFilterId;
                        return plan.phaseId === nextPhaseFilterId && plan.methodId === selectedMethodFilterId;
                      });

                      if (!methodStillAvailable) {
                        setSelectedMethodFilterId('');
                      }
                    }

                    requestAnimationFrame(() => centerGraphViewport());
                  }}
                >
                  <MenuItem value="">Phase: todas</MenuItem>
                  {verificationPlanFilterCatalog.phases.map((phaseOption) => (
                    <MenuItem key={phaseOption.id} value={phaseOption.id}>{phaseOption.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 210 }}>
                <Select
                  displayEmpty
                  value={selectedMethodFilterId}
                  onChange={(event) => {
                    setSelectedMethodFilterId(event.target.value);
                    requestAnimationFrame(() => centerGraphViewport());
                  }}
                >
                  <MenuItem value="">Metodo: todos</MenuItem>
                  {availableMethodFilterOptions.map((methodOption) => (
                    <MenuItem key={methodOption.id} value={methodOption.id}>{methodOption.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {hasActivePlanFilters && (
                <Chip size="small" color="primary" label={`VP visibles: ${filteredVerificationPlanIds.size}`} />
              )}

              {hasActivePlanFilters && filteredVerificationPlanIds.size === 0 && (
                <Chip size="small" color="warning" label="Sin VerificationPlan para esos filtros" />
              )}

              <Button
                size="small"
                variant="outlined"
                disabled={!hasActivePlanFilters}
                onClick={() => {
                  resetGeneralGraphFilters();
                  requestAnimationFrame(() => centerGraphViewport());
                }}
              >
                Limpiar filtros
              </Button>

              <Button size="small" variant="outlined" onClick={handleGraphNodePositionsReset}>
                Reset nodos
              </Button>
            </Stack>
          </Stack>
        )}

        <Box
          ref={graphContainerRef}
          onMouseDown={handleGraphMouseDown}
          onMouseMove={handleGraphMouseMove}
          onMouseUp={handleGraphMouseUpOrLeave}
          onMouseLeave={handleGraphMouseUpOrLeave}
          sx={{
            overflow: 'auto',
            height: viewportHeight,
            cursor: isGraphDragging ? 'grabbing' : 'grab',
            userSelect: 'none'
          }}
        >
          <svg width={svgWidth} height={svgHeight} role="img" aria-label="Diagrama general de clases, instancias, verification plans y requirements">
            {renderedEdges.map((edge) => {
              const source = nodeRenderMap[edge.from];
              const target = nodeRenderMap[edge.to];
              if (!source || !target) return null;

              const hasSelectedNode = !!selectedGeneralNodeId;
              const isConnectedToSelection = hasSelectedNode
                && (edge.from === selectedGeneralNodeId || edge.to === selectedGeneralNodeId);
              const hasFocus = hasSelectedNode;
              const isFocusedEdge = isConnectedToSelection;

              const sameLane = source.y === target.y;
              const x1 = sameLane
                ? (source.x <= target.x ? source.x + source.width : source.x)
                : source.x + (source.width / 2);
              const y1 = sameLane
                ? source.y + (source.height / 2)
                : source.y + source.height;
              const x2 = sameLane
                ? (source.x <= target.x ? target.x : target.x + target.width)
                : target.x + (target.width / 2);
              const y2 = sameLane
                ? target.y + (target.height / 2)
                : target.y;
              const midY = y1 + ((y2 - y1) / 2);
              const midX = x1 + ((x2 - x1) / 2);

              const edgeColor = edge.type === 'root-class'
                ? '#2563eb'
                : (edge.type === 'class-instance' || edge.type === 'object-class')
                  ? '#16a34a'
                  : (edge.type === 'instance-plan' || edge.type === 'class-plan')
                    ? '#d97706'
                    : edge.type === 'plan-requirement'
                      ? '#7c3aed'
                      : '#dc2626';

              const pathD = sameLane
                ? `M ${x1} ${y1} C ${midX} ${y1 - 26}, ${midX} ${y2 + 26}, ${x2} ${y2}`
                : `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;

              return (
                <path
                  key={`${edge.from}->${edge.to}:${edge.type}`}
                  d={pathD}
                  fill="none"
                  stroke={edgeColor}
                  strokeWidth={!hasFocus ? '1.3' : (isFocusedEdge ? '2.7' : '0.9')}
                  strokeOpacity={!hasFocus ? '0.55' : (isFocusedEdge ? '0.97' : '0.08')}
                />
              );
            })}

            {renderedNodes.map((node) => {
              const isSelected = selectedGeneralNodeId === node.id;
              const isNeighbor = !isSelected && !!selectedGeneralNodeId &&
                (generalDiagramData.adjacentByNode[selectedGeneralNodeId]?.has(node.id));

              const resolvedNodeType = node.nodeType
                || (node.lane === 'roots'
                  ? 'root'
                  : node.lane === 'classes'
                    ? 'class'
                    : node.lane === 'instances' || node.lane === 'objectClass'
                      ? 'instance'
                      : node.lane === 'plans'
                        ? 'plan'
                        : 'requirement');

              const baseFill = resolvedNodeType === 'root'
                ? '#dbeafe'
                : resolvedNodeType === 'class'
                  ? '#dcfce7'
                  : resolvedNodeType === 'instance'
                    ? '#e0f2fe'
                    : resolvedNodeType === 'plan'
                      ? '#ede9fe'
                      : '#fee2e2';

              const baseStroke = resolvedNodeType === 'root'
                ? '#1d4ed8'
                : resolvedNodeType === 'class'
                  ? '#15803d'
                  : resolvedNodeType === 'instance'
                    ? '#0e7490'
                    : resolvedNodeType === 'plan'
                      ? '#6d28d9'
                      : '#b91c1c';

              const fill = isSelected ? '#fde68a' : baseFill;

              const stroke = isSelected ? '#d97706' : baseStroke;

              const shortLabel = node.label.length > 30 ? `${node.label.slice(0, 29)}...` : node.label;
              const shortId = node.id.length > 34 ? `${node.id.slice(0, 33)}...` : node.id;

              return (
                <g
                  key={node.id}
                  onMouseDown={enableSelection ? (event) => handleGraphNodeMouseDown(event, node) : undefined}
                  onClick={enableSelection ? () => handleGeneralGraphNodeClick(node.id) : undefined}
                  style={{
                    cursor: enableSelection
                      ? (draggingNodeId === node.id ? 'grabbing' : 'grab')
                      : 'default'
                  }}
                >
                  <rect
                    x={node.x}
                    y={node.y}
                    rx="9"
                    ry="9"
                    width={node.width}
                    height={node.height}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={isSelected ? '2.4' : (isNeighbor ? '2' : '1.5')}
                    opacity={!selectedGeneralNodeId || isSelected || isNeighbor ? 1 : 0.32}
                  />
                  <text x={node.x + 9} y={node.y + 19} fontSize="12" fontWeight="700" fill="#0f172a">
                    {shortLabel}
                  </text>
                  <text x={node.x + 9} y={node.y + 34} fontSize="10" fill="#475569" style={{ fontFamily: 'monospace' }}>
                    {shortId}
                  </text>
                  <title>{`${node.label}\n${node.id}`}</title>
                </g>
              );
            })}
          </svg>
        </Box>
      </Box>
    );
  };

  const renderClassInheritanceGraphCanvas = (options = {}) => {
    const viewportHeight = options.viewportHeight || '72vh';
    const graphNodes = classInheritanceGraphData.nodes;
    const graphEdges = classInheritanceGraphData.edges;
    const graphNodeMap = classInheritanceGraphData.nodeMap;

    const nodeStylesByType = {
      root: { fill: '#dbeafe', stroke: '#1d4ed8' },
      class: { fill: '#dcfce7', stroke: '#15803d' },
      external: { fill: '#fef3c7', stroke: '#d97706' },
      aux: { fill: '#fee2e2', stroke: '#b91c1c' }
    };

    return (
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#f8fafc' }}>
        <Box sx={{ px: 1, py: 0.75, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="caption" color="text.secondary">
            Grafo completo de herencia por rdfs:subClassOf. Flujo de arriba hacia abajo.
          </Typography>
        </Box>

        <Box
          sx={{
            overflow: 'auto',
            height: viewportHeight,
            userSelect: 'none'
          }}
        >
          <svg
            width={classInheritanceGraphData.width}
            height={classInheritanceGraphData.height}
            role="img"
            aria-label="Grafo completo de herencia de clases"
          >
            {graphEdges.map((edge) => {
              const source = graphNodeMap[edge.from];
              const target = graphNodeMap[edge.to];
              if (!source || !target) return null;

              const x1 = source.x + (source.width / 2);
              const y1 = source.y + source.height;
              const x2 = target.x + (target.width / 2);
              const y2 = target.y;
              const midY = y1 + ((y2 - y1) / 2);
              const pathD = `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;

              return (
                <path
                  key={`${edge.from}->${edge.to}`}
                  d={pathD}
                  fill="none"
                  stroke="#475569"
                  strokeWidth="1.35"
                  strokeOpacity="0.52"
                />
              );
            })}

            {graphNodes.map((node) => {
              const nodeStyle = nodeStylesByType[node.nodeType] || nodeStylesByType.class;
              const shortLabel = node.label.length > 34 ? `${node.label.slice(0, 33)}...` : node.label;
              const shortId = node.id.length > 42 ? `${node.id.slice(0, 41)}...` : node.id;

              return (
                <g key={node.id}>
                  <rect
                    x={node.x}
                    y={node.y}
                    rx="9"
                    ry="9"
                    width={node.width}
                    height={node.height}
                    fill={nodeStyle.fill}
                    stroke={nodeStyle.stroke}
                    strokeWidth="1.55"
                  />
                  <text x={node.x + 9} y={node.y + 20} fontSize="12" fontWeight="700" fill="#0f172a">
                    {shortLabel}
                  </text>
                  <text x={node.x + 9} y={node.y + 36} fontSize="10" fill="#475569" style={{ fontFamily: 'monospace' }}>
                    {shortId}
                  </text>
                  <title>{`${node.label}\n${node.id}`}</title>
                </g>
              );
            })}
          </svg>
        </Box>
      </Box>
    );
  };

  const renderSelectedGeneralNodeInfoPanel = (options = {}) => {
    if (!selectedGeneralNode) return null;

    const panelTitle = options.title || 'Nodo seleccionado';
    const maxHeight = options.maxHeight || '72vh';
    const detailKind = selectedGeneralNodeDetails?.kind || 'basic';
    const detailData = selectedGeneralNodeDetails?.data || {};
    const subtleIdSx = {
      display: 'block',
      fontFamily: 'monospace',
      color: 'text.disabled',
      fontSize: '0.68rem',
      lineHeight: 1.25
    };

    const renderSubtleId = (idValue, referenceValue, sx = {}) => {
      const idText = (idValue || '').toString().trim();
      const refText = (referenceValue || '').toString().trim();

      if (!idText || idText === refText) return null;

      return (
        <Typography variant="caption" sx={{ ...subtleIdSx, ...sx }}>
          {idText}
        </Typography>
      );
    };

    const renderPlanRelation = (predicateLabel, valueLabel, idValue, extra = null) => (
      <Box sx={{ pl: 1, borderLeft: '3px solid #e2e8f0' }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
          {predicateLabel}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {valueLabel || '-'}
        </Typography>
        {renderSubtleId(idValue, valueLabel, { mt: 0.2 })}
        {extra}
      </Box>
    );

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          height: '100%',
          maxHeight,
          overflowY: 'auto'
        }}
      >
        <Stack spacing={1.2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {panelTitle}
            </Typography>
            <Button size="small" variant="outlined" onClick={handleCloseGeneralNodePanel}>
              Cerrar
            </Button>
          </Stack>

          {detailKind === 'plan' ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                VerificationPlan
              </Typography>
              {renderSubtleId(selectedGeneralNode.id, '')}
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {selectedGeneralNode.label}
              </Typography>
              {renderSubtleId(selectedGeneralNode.id, selectedGeneralNode.label)}
            </>
          )}

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip size="small" label={`Tipo: ${selectedGeneralNode.lane}`} />
            <Chip
              size="small"
              label={`Conexiones: ${selectedGeneralConnections.incoming.length + selectedGeneralConnections.outgoing.length}`}
              color="primary"
              variant="outlined"
            />
          </Stack>

          {detailKind === 'object' && (
            <Stack spacing={1}>
              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                <Typography variant="body2"><strong>Codigo:</strong> {detailData.notation || '-'}</Typography>
                <Typography variant="body2"><strong>Nombre:</strong> {detailData.label || '-'}</Typography>
                {renderSubtleId(detailData.id, detailData.label, { mt: 0.15 })}
                <Typography variant="body2">
                  <strong>Tipo raiz:</strong> {detailData.rootTypes?.length > 0 ? detailData.rootTypes.map((item) => item.label).join(', ') : '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Clase(s):</strong> {detailData.classTypes?.length > 0 ? detailData.classTypes.map((item) => item.label).join(' | ') : '-'}
                </Typography>
                {detailData.classParent && (
                  <Typography variant="body2"><strong>Clase padre:</strong> {detailData.classParent.label}</Typography>
                )}
                {detailData.classChild && (
                  <Typography variant="body2"><strong>Subclase:</strong> {detailData.classChild.label}</Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.6 }}>
                  Atributos tennet ({detailData.attributes?.length || 0})
                </Typography>

                {detailData.attributes?.length > 0 ? (
                  <Stack spacing={0.9}>
                    {detailData.attributes.map((attribute) => (
                      <Box key={`${detailData.id}-${attribute.id}`} sx={{ pl: 1, borderLeft: '3px solid #cbd5e1' }}>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <strong>{attribute.label}</strong>
                          {attribute.typeLabel ? ` (${attribute.typeLabel})` : ''}
                        </Typography>
                        {renderSubtleId(attribute.id, attribute.label, { mb: 0.25 })}
                        {attribute.values.map((value, index) => (
                          <Typography key={`${attribute.id}-${index}`} variant="caption" sx={{ display: 'block' }}>
                            - {value.label}
                            {!value.isLiteral && value.id && (
                              <Typography component="span" sx={{ ml: 0.75, ...subtleIdSx }}>
                                ({value.id})
                              </Typography>
                            )}
                          </Typography>
                        ))}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">Sin atributos tennet detectados.</Typography>
                )}
              </Paper>
            </Stack>
          )}

          {detailKind === 'class' && (
            <Stack spacing={1}>
              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                <Typography variant="body2"><strong>Nombre:</strong> {detailData.label || '-'}</Typography>
                {renderSubtleId(detailData.id, detailData.label, { mt: 0.15 })}
                <Typography variant="body2">
                  <strong>{detailData.classCodePropertyLabel || 'Codigo de clase'}:</strong> {detailData.classCode || '-'}
                </Typography>
                {detailData.classCodePropertyId && (
                  <Typography variant="caption" sx={subtleIdSx}>
                    {detailData.classCodePropertyId}
                  </Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  SubClassOf ({detailData.subClassOf?.length || 0})
                </Typography>
                {detailData.subClassOf?.length > 0 ? (
                  <Stack spacing={0.6}>
                    {detailData.subClassOf.map((parent) => (
                      <Typography key={`${detailData.id}-${parent.id}`} variant="caption" sx={{ display: 'block' }}>
                        - {parent.label}
                        <Typography component="span" color="text.secondary" sx={{ ml: 0.75, fontFamily: 'monospace' }}>
                          ({parent.id})
                        </Typography>
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">Sin relaciones subClassOf.</Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Requirements ({detailData.requirements?.length || 0})
                </Typography>
                {detailData.requirements?.length > 0 ? (
                  <Stack spacing={0.6}>
                    {detailData.requirements.map((req) => (
                      <Typography key={`${detailData.id}-${req.id}`} variant="caption" sx={{ display: 'block' }}>
                        - {req.label}
                        <Typography component="span" color="text.secondary" sx={{ ml: 0.75, fontFamily: 'monospace' }}>
                          ({req.id})
                        </Typography>
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">Sin hasRequirement.</Typography>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Propiedades SH ({detailData.properties?.length || 0})
                </Typography>
                {detailData.properties?.length > 0 ? (
                  <Stack spacing={0.8}>
                    {detailData.properties.map((prop, index) => (
                      <Box key={`${detailData.id}-${prop.pathId}-${index}`} sx={{ pl: 1, borderLeft: '3px solid #cbd5e1' }}>
                        <Typography variant="caption" sx={{ display: 'block' }}><strong>Path:</strong> {prop.pathLabel}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                          {prop.pathId}
                        </Typography>
                        {prop.minCount !== null && (
                          <Typography variant="caption" sx={{ display: 'block' }}><strong>minCount:</strong> {prop.minCount}</Typography>
                        )}
                        {prop.hasValue && (
                          <Typography variant="caption" sx={{ display: 'block' }}><strong>hasValue:</strong> {prop.hasValue}</Typography>
                        )}
                        {prop.classId && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            <strong>class:</strong> {prop.classLabel}
                            <Typography component="span" color="text.secondary" sx={{ ml: 0.75, fontFamily: 'monospace' }}>
                              ({prop.classId})
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">Sin bloques sh:property.</Typography>
                )}
              </Paper>
            </Stack>
          )}

          {detailKind === 'plan' && (
            <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
              <Stack spacing={0.95}>
                <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, bgcolor: '#f8fafc' }}>
                  <Stack spacing={0.65}>
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                        Metodo
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {detailData.methodLabel || detailData.label || '-'}
                      </Typography>
                      {renderSubtleId(detailData.methodId, detailData.methodLabel || detailData.label || '-', { mt: 0.2 })}
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                        Fase
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {detailData.phaseLabel || '-'}
                      </Typography>
                      {renderSubtleId(detailData.phaseId, detailData.phaseLabel || '-', { mt: 0.2 })}
                    </Box>
                  </Stack>
                </Paper>

                {renderPlanRelation(
                  'tennet:hasAsSubject',
                  detailData.subjectLabel || '-',
                  detailData.subjectId,
                  detailData.hasAsSubject?.types?.length > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                      type: {detailData.hasAsSubject.types.map((typeItem) => typeItem.label).join(', ')}
                    </Typography>
                  ) : null
                )}

                {renderPlanRelation(
                  'tennet:isVerificationOf',
                  detailData.requirementLabel || '-',
                  detailData.requirementId
                )}
              </Stack>
            </Paper>
          )}

          {detailKind === 'requirement' && (
            <Stack spacing={1.5}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                <Typography variant="body2"><strong>Codigo:</strong> {detailData.notation}</Typography>
                <Typography variant="body2"><strong>Nombre:</strong> {detailData.name}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  <strong>Norma:</strong> {detailData.norm}
                </Typography>
              </Paper>

              {(detailData.fields || []).map((field) => (
                <Paper key={`${detailData.id}-${field.id}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {field.typeLabel}: {field.fieldName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {field.id}
                  </Typography>
                  <Stack spacing={0.5}>
                    {field.values.map((value, index) => (
                      <Typography key={`${field.id}-${index}`} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        - {value}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              ))}

              {detailData.refersToDocuments && detailData.refersToDocuments.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                    Documentos referenciados ({detailData.refersToDocuments.length})
                  </Typography>
                  <Stack spacing={1}>
                    {detailData.refersToDocuments.map((doc) => (
                      <Box key={doc.id} sx={{ pl: 1, borderLeft: '3px solid #1976d2' }}>
                        <Typography variant="body2"><strong>Nombre:</strong> {doc.name}</Typography>
                        {doc.definition && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            <strong>Definicion:</strong> {doc.definition}
                          </Typography>
                        )}
                        {doc.seeAlso && (
                          <Typography variant="body2">
                            <strong>Versión:</strong>{' '}
                            <a href={doc.seeAlso} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{doc.seeAlso}</a>
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">{doc.id}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}

              {detailData.hasAsSourceDocuments && detailData.hasAsSourceDocuments.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                    Fuente (hasAsSource) ({detailData.hasAsSourceDocuments.length})
                  </Typography>
                  <Stack spacing={1}>
                    {detailData.hasAsSourceDocuments.map((doc) => (
                      <Box key={doc.id} sx={{ pl: 1, borderLeft: '3px solid #2e7d32' }}>
                        <Typography variant="body2"><strong>Nombre:</strong> {doc.name}</Typography>
                        {doc.definition && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            <strong>Definicion:</strong> {doc.definition}
                          </Typography>
                        )}
                        {doc.seeAlso && (
                          <Typography variant="body2">
                            <strong>Versión:</strong>{' '}
                            <a href={doc.seeAlso} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{doc.seeAlso}</a>
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">{doc.id}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}

          {detailKind === 'basic' && (
            <Alert severity="info">No hay detalle extendido para este tipo de nodo.</Alert>
          )}

          <Divider />

          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Conexiones salientes ({selectedGeneralConnections.outgoing.length + selectedGeneralConnections.outgoingOverflow})
          </Typography>
          <Stack spacing={0.75}>
            {selectedGeneralConnections.outgoing.length > 0 ? selectedGeneralConnections.outgoing.map((connection) => (
              <Paper key={connection.id} variant="outlined" sx={{ p: 0.75, borderRadius: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  <Chip size="small" label={connection.edgeLabel} variant="outlined" />
                  <Button size="small" variant="text" onClick={() => handleGeneralNodeSelect(connection.nodeId)}>
                    {connection.nodeLabel}
                  </Button>
                </Stack>
              </Paper>
            )) : (
              <Typography variant="caption" color="text.secondary">Sin conexiones salientes.</Typography>
            )}
            {selectedGeneralConnections.outgoingOverflow > 0 && (
              <Typography variant="caption" color="text.secondary">
                +{selectedGeneralConnections.outgoingOverflow} conexiones adicionales no mostradas.
              </Typography>
            )}
          </Stack>

          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Conexiones entrantes ({selectedGeneralConnections.incoming.length + selectedGeneralConnections.incomingOverflow})
          </Typography>
          <Stack spacing={0.75}>
            {selectedGeneralConnections.incoming.length > 0 ? selectedGeneralConnections.incoming.map((connection) => (
              <Paper key={connection.id} variant="outlined" sx={{ p: 0.75, borderRadius: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  <Chip size="small" label={connection.edgeLabel} variant="outlined" />
                  <Button size="small" variant="text" onClick={() => handleGeneralNodeSelect(connection.nodeId)}>
                    {connection.nodeLabel}
                  </Button>
                </Stack>
              </Paper>
            )) : (
              <Typography variant="caption" color="text.secondary">Sin conexiones entrantes.</Typography>
            )}
            {selectedGeneralConnections.incomingOverflow > 0 && (
              <Typography variant="caption" color="text.secondary">
                +{selectedGeneralConnections.incomingOverflow} conexiones adicionales no mostradas.
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
      <Stack spacing={3}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Vista X - VerificationPlan
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Carga un archivo TTL para generar el arbol por fase y metodo de verificacion.
          </Typography>
        </Box>

        <Stack spacing={2} alignItems="center">
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant={viewMode === 'verification' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('verification')}
            >
              Vista VerificationPlan
            </Button>
            <Button
              variant={viewMode === 'requirements' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('requirements')}
            >
              Vista Requirements
            </Button>
            <Button
              variant={viewMode === 'ontology' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('ontology')}
            >
              Vista Ontologia
            </Button>
            <Button
              variant={viewMode === 'ontologyMatch' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('ontologyMatch')}
            >
              Vista Cruce Ontologia
            </Button>
            <Button
              variant={viewMode === 'ontologyMissing' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('ontologyMissing')}
            >
              Vista IDs faltantes
            </Button>
            <Button
              variant={viewMode === 'general' ? 'contained' : 'outlined'}
              onClick={() => {
                setGeneralObjectFocusMode(false);
                setGeneralFocusAnchorNodeId('');
                setIsObjectGraphDialogOpen(false);
                setViewMode('general');
              }}
            >
              Vista General
            </Button>
            <Button
              variant={viewMode === 'objects' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('objects')}
            >
              Vista Objeto
            </Button>
            <Button
              variant={viewMode === 'classes' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('classes')}
            >
              Vista Clases
            </Button>
          </Stack>

          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
            sx={{ minWidth: 220 }}
          >
            Seleccionar archivo TTL
            <input
              hidden
              type="file"
              accept=".ttl,text/turtle"
              onChange={handleFileSelect}
            />
          </Button>

          {selectedFile && (
            <Chip
              color="primary"
              variant="outlined"
              label={`${selectedFile.name} (${formatFileSize(selectedFile.size)})`}
            />
          )}

          <Button
            variant="contained"
            startIcon={<Insights />}
            disabled={!canUpload || isProcessing}
            onClick={handlePrepareUpload}
          >
            {isProcessing
              ? 'Procesando TTL...'
              : viewMode === 'verification'
                ? 'Generar arbol de VerificationPlan'
                : viewMode === 'requirements'
                  ? 'Cargar vista de Requirements'
                  : viewMode === 'ontology'
                    ? 'Cargar vista de Ontologias'
                    : viewMode === 'ontologyMatch'
                      ? 'Procesar TTL base para cruce'
                      : viewMode === 'ontologyMissing'
                        ? 'Procesar TTL base para IDs faltantes'
                  : viewMode === 'general'
                    ? 'Generar vista general'
                    : viewMode === 'objects'
                      ? 'Cargar vista de Objetos'
                  : 'Cargar vista de Clases'}
          </Button>
        </Stack>

        {isPrepared && selectedFile && (
          <Alert severity="success">
            Archivo procesado correctamente.
          </Alert>
        )}

        {viewMode === 'verification' && verificationSummary.plans > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${verificationSummary.plans} VerificationPlan`} color="primary" />
              <Chip label={`${verificationSummary.phases} fases`} variant="outlined" />
              <Chip label={`${verificationSummary.methods} metodos`} variant="outlined" />
            </Stack>
          </Paper>
        )}

        {viewMode === 'requirements' && requirementsSummary.requirements > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${requirementsSummary.requirements} Requirements`} color="primary" />
              <Chip label={`${requirementsSummary.annotationFields} AnnotationProperty`} variant="outlined" />
              <Chip label={`${requirementsSummary.controlFields} Control`} variant="outlined" />
            </Stack>
          </Paper>
        )}

        {viewMode === 'ontology' && ontologiesSummary.ontologies > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${ontologiesSummary.ontologies} Ontologias`} color="primary" />
              <Chip label={`${ontologiesSummary.withDate} con dcterms:date`} variant="outlined" />
              <Chip label={`${ontologiesSummary.withVersionInfo} con owl:versionInfo`} variant="outlined" />
              <Chip label={`${ontologiesSummary.withPrefLabel} con skos:prefLabel`} variant="outlined" />
            </Stack>
          </Paper>
        )}

        {viewMode === 'ontologyMatch' && ontologyMatchState.hasCompared && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${ontologyMatchState.sharedIdsCount} IDs coincidentes`} color="primary" />
              <Chip label={`${ontologyMatchState.principalSharedIdsCount} coincidencias principales`} variant="outlined" />
              <Chip label={`${ontologyMatchState.nonPrincipalSharedIdsCount} coincidencias por referencia`} variant="outlined" />
              <Chip label={`${ontologyMatchState.sourceUniqueIds} IDs en archivo base`} variant="outlined" />
              <Chip label={`${ontologyMatchState.ontologyUniqueIds} IDs en ontologia`} variant="outlined" />
              <Chip label={`${ontologyMatchState.sourceOnlyCount} solo en base`} variant="outlined" />
              <Chip label={`${ontologyMatchState.ontologyOnlyCount} solo en ontologia`} variant="outlined" />
            </Stack>
          </Paper>
        )}

        {viewMode === 'ontologyMissing' && ontologyMissingState.hasCompared && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${ontologyMissingState.missingIdsCount} IDs faltantes`} color="warning" />
              <Chip label={`${ontologyMissingState.coveredIdsCount} IDs cubiertos`} color="success" variant="outlined" />
              <Chip label={`${ontologyMissingState.sourceUniqueIds} IDs principales en archivo base`} variant="outlined" />
              <Chip label={`${ontologyMissingState.ontologyFilesCount} ontologias comparadas`} variant="outlined" />
            </Stack>
          </Paper>
        )}

        {viewMode === 'classes' && classesSummary.classes > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${classesSummary.classes} Clases`} color="primary" />
              <Chip label={`${classesSummary.inheritanceLinks} enlaces subClassOf`} variant="outlined" />
              <Chip label={`${classesSummary.roots} raices del grafo`} variant="outlined" />
            </Stack>
          </Paper>
        )}

        {viewMode === 'objects' && objectsSummary.objects > 0 && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${objectsSummary.objects} Objetos`} color="primary" />
              <Chip label={`${objectsSummary.realObjects} RealObject`} variant="outlined" />
              <Chip label={`${objectsSummary.spatialRegions} SpatialRegion`} variant="outlined" />
              <Chip label={`${objectsSummary.withClassAndSubclass} con clase + subclase`} variant="outlined" />
            </Stack>
          </Paper>
        )}

        {viewMode === 'general' && isPrepared && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${generalDiagramData.summary.roots} raices`} color="primary" />
              <Chip label={`${generalDiagramData.summary.classes} clases`} variant="outlined" />
              <Chip label={`${generalDiagramData.summary.instances} objetos/regiones`} variant="outlined" />
              <Chip label={`${generalDiagramData.summary.plans} verificationPlans`} variant="outlined" />
              <Chip label={`${generalDiagramData.summary.requirements} requirements conectados`} variant="outlined" />
            </Stack>
          </Paper>
        )}

        {viewMode === 'verification' && verificationTreeData.length > 0 && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={handleExpandAll}>
                  Expandir todo
                </Button>
                <Button size="small" variant="outlined" onClick={handleCollapseAll}>
                  Contraer todo
                </Button>
              </Stack>
            </Paper>

            {verificationTreeData.map((phaseNode) => (
              <Paper key={phaseNode.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Button
                  fullWidth
                  onClick={() => togglePhase(phaseNode.id)}
                  sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    px: 0,
                    py: 0.5,
                    color: 'inherit'
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    {expandedPhases[phaseNode.id] ? <ExpandMore /> : <ChevronRight />}
                    <AccountTree fontSize="small" />
                    <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                        Fase: {phaseNode.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {phaseNode.id} | {phaseNode.plansCount} VerificationPlan
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip size="small" label={`${phaseNode.methods.length} metodos`} variant="outlined" />
                </Button>

                <Collapse in={!!expandedPhases[phaseNode.id]} timeout="auto" unmountOnExit>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={1}>
                    {phaseNode.methods.map((methodNode) => {
                      const methodKey = getMethodKey(phaseNode.id, methodNode.id);

                      return (
                        <Paper key={methodNode.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                          <Button
                            fullWidth
                            onClick={() => toggleMethod(phaseNode.id, methodNode.id)}
                            sx={{
                              justifyContent: 'space-between',
                              textTransform: 'none',
                              px: 0,
                              py: 0.25,
                              color: 'inherit'
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                              {expandedMethods[methodKey] ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                              <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                                  Metodo: {methodNode.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {methodNode.id}
                                </Typography>
                              </Box>
                            </Stack>
                            <Chip size="small" label={`${methodNode.plans.length} plans`} />
                          </Button>

                          <Collapse in={!!expandedMethods[methodKey]} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                mt: 1,
                                pl: 2,
                                borderLeft: '2px solid #d0d0d0',
                                maxHeight: 220,
                                overflowY: 'auto'
                              }}
                            >
                              <Stack spacing={0.5}>
                                {methodNode.plans.map((planId) => (
                                  <Paper key={planId.id} variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                                    <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                                      {planId.id}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                      <strong>hasAsSubject:</strong> {planId.hasAsSubject.label}
                                    </Typography>
                                    {planId.hasAsSubject.id && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                        {planId.hasAsSubject.id}
                                      </Typography>
                                    )}
                                    {planId.hasAsSubject.types.length > 0 && (
                                      <Typography variant="caption" sx={{ display: 'block' }}>
                                        <strong>type:</strong> {planId.hasAsSubject.types.map((typeItem) => typeItem.label).join(', ')}
                                      </Typography>
                                    )}
                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                      <strong>isVerificationOf:</strong> {planId.isVerificationOf.label}
                                    </Typography>
                                    {planId.isVerificationOf.id && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                        {planId.isVerificationOf.id}
                                      </Typography>
                                    )}
                                  </Paper>
                                ))}
                              </Stack>
                            </Box>
                          </Collapse>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Collapse>
              </Paper>
            ))}
          </Stack>
        )}

        {viewMode === 'requirements' && groupedRequirements.length > 0 && (
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={handleExpandAllVakgebied}>
                  Expandir Vakgebied
                </Button>
                <Button size="small" variant="outlined" onClick={handleCollapseAllVakgebied}>
                  Contraer Vakgebied
                </Button>
              </Stack>
            </Paper>

            {groupedRequirements.map((group) => (
              <Paper key={group.name} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Button
                  fullWidth
                  onClick={() => handleVakgebiedToggle(group.name)}
                  sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    px: 0,
                    py: 0.25,
                    color: 'inherit'
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    {expandedVakgebiedGroups[group.name] ? <ExpandMore /> : <ChevronRight />}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      AnnotationProperty: Vakgebied - {group.name}
                    </Typography>
                  </Stack>
                  <Chip size="small" label={`${group.requirements.length} requisitos`} />
                </Button>

                <Collapse in={!!expandedVakgebiedGroups[group.name]} timeout="auto" unmountOnExit>
                  <Stack spacing={1} sx={{ mt: 1.25 }}>
                    {group.requirements.map((requirement) => (
                      <Paper key={requirement.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                        <Button
                          fullWidth
                          onClick={() => handleRequirementToggle(requirement.id)}
                          sx={{
                            justifyContent: 'space-between',
                            textTransform: 'none',
                            px: 0,
                            py: 0.25,
                            color: 'inherit'
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            {expandedRequirements[requirement.id] ? <ExpandMore /> : <ChevronRight />}
                            <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                                {requirement.notation} - {requirement.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {requirement.id}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip size="small" label={`${requirement.fields.length} campos`} />
                        </Button>

                        <Collapse in={!!expandedRequirements[requirement.id]} timeout="auto" unmountOnExit>
                          <Divider sx={{ my: 1.5 }} />
                          <Stack spacing={1.5}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                              <Typography variant="body2"><strong>Codigo:</strong> {requirement.notation}</Typography>
                              <Typography variant="body2"><strong>Nombre:</strong> {requirement.name}</Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                <strong>Norma:</strong> {requirement.norm}
                              </Typography>
                            </Paper>

                            {requirement.fields.map((field) => (
                              <Paper key={`${requirement.id}-${field.id}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.25 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                  {field.typeLabel}: {field.fieldName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  {field.id}
                                </Typography>
                                <Stack spacing={0.5}>
                                  {field.values.map((value, index) => (
                                    <Typography key={`${field.id}-${index}`} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      - {value}
                                    </Typography>
                                  ))}
                                </Stack>
                              </Paper>
                            ))}

                            {requirement.refersToDocuments && requirement.refersToDocuments.length > 0 && (
                              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                                  Documentos referenciados ({requirement.refersToDocuments.length})
                                </Typography>
                                <Stack spacing={1}>
                                  {requirement.refersToDocuments.map((doc) => (
                                    <Box key={doc.id} sx={{ pl: 1, borderLeft: '3px solid #1976d2' }}>
                                      <Typography variant="body2"><strong>Nombre:</strong> {doc.name}</Typography>
                                      {doc.definition && (
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                          <strong>Definicion:</strong> {doc.definition}
                                        </Typography>
                                      )}
                                      {doc.seeAlso && (
                                        <Typography variant="body2">
                                          <strong>Versión:</strong>{' '}
                                          <a href={doc.seeAlso} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{doc.seeAlso}</a>
                                        </Typography>
                                      )}
                                      <Typography variant="caption" color="text.secondary">{doc.id}</Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </Paper>
                            )}

                            {requirement.hasAsSourceDocuments && requirement.hasAsSourceDocuments.length > 0 && (
                              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                                  Fuente (hasAsSource) ({requirement.hasAsSourceDocuments.length})
                                </Typography>
                                <Stack spacing={1}>
                                  {requirement.hasAsSourceDocuments.map((doc) => (
                                    <Box key={doc.id} sx={{ pl: 1, borderLeft: '3px solid #2e7d32' }}>
                                      <Typography variant="body2"><strong>Nombre:</strong> {doc.name}</Typography>
                                      {doc.definition && (
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                          <strong>Definicion:</strong> {doc.definition}
                                        </Typography>
                                      )}
                                      {doc.seeAlso && (
                                        <Typography variant="body2">
                                          <strong>Versión:</strong>{' '}
                                          <a href={doc.seeAlso} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>{doc.seeAlso}</a>
                                        </Typography>
                                      )}
                                      <Typography variant="caption" color="text.secondary">{doc.id}</Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </Paper>
                            )}
                          </Stack>
                        </Collapse>
                      </Paper>
                    ))}
                  </Stack>
                </Collapse>
              </Paper>
            ))}
          </Stack>
        )}

        {viewMode === 'ontology' && isPrepared && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Vista de ontologias (owl:Ontology)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Se listan todos los recursos con rdf:type owl:Ontology detectados en el TTL, incluyendo prefLabel, fecha, version y el resto de metadatos publicados.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AccountTree fontSize="small" />}
                  onClick={handleOpenClassInheritanceGraphDialog}
                  disabled={classInheritanceGraphData.summary.classes === 0}
                >
                  Ver grafo de herencia de clases
                </Button>
              </Stack>
            </Paper>

            {ontologiesData.length === 0 ? (
              <Alert severity="info">
                No se detectaron recursos rdf:type owl:Ontology en el archivo cargado.
              </Alert>
            ) : (
              <Stack spacing={1.25}>
                {ontologiesData.map((ontology) => (
                  <Paper key={ontology.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <Stack spacing={1.1}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {ontology.prefLabel || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {ontology.id}
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Chip size="small" label={`Fecha: ${ontology.date || '-'}`} variant="outlined" />
                        <Chip size="small" label={`Version: ${ontology.versionInfo || '-'}`} variant="outlined" />
                      </Stack>

                      {ontology.metadata.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          Sin metadatos adicionales.
                        </Typography>
                      ) : (
                        <Stack spacing={0.9}>
                          {ontology.metadata.map((metadata) => (
                            <Paper key={`${ontology.id}-${metadata.id}`} variant="outlined" sx={{ p: 1.1, borderRadius: 1.1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {metadata.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.4, fontFamily: 'monospace' }}>
                                {metadata.id}
                              </Typography>
                              <Stack spacing={0.45}>
                                {metadata.values.map((value, index) => (
                                  <Typography key={`${ontology.id}-${metadata.id}-${index}`} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    - {value}
                                  </Typography>
                                ))}
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Stack>
        )}

        {viewMode === 'ontologyMatch' && isPrepared && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Cruce de IDs con TTL de ontologia
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Carga un segundo TTL (ontologia) y busca coincidencias por UUID entre ambos archivos. Se priorizan coincidencias donde el UUID aparece como sujeto principal, pero tambien se incluyen coincidencias por referencia cuando aplique. Ejemplo: &lt;http://data.tennet.eu/def/8ee71f57-0a44-3755-a9f8-92d2a4d606fa&gt; se normaliza a 8ee71f57-0a44-3755-a9f8-92d2a4d606fa.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                >
                  Seleccionar TTL de ontologia
                  <input
                    hidden
                    type="file"
                    accept=".ttl,text/turtle"
                    onChange={handleOntologyFileSelect}
                  />
                </Button>

                {selectedOntologyFile && (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`${selectedOntologyFile.name} (${formatFileSize(selectedOntologyFile.size)})`}
                  />
                )}

                <Button
                  size="small"
                  variant="contained"
                  disabled={!sourceTtlText || !selectedOntologyFile || isOntologyMatchProcessing}
                  onClick={handleRunOntologyIdMatch}
                >
                  {isOntologyMatchProcessing ? 'Buscando coincidencias...' : 'Buscar coincidencias de IDs'}
                </Button>
              </Stack>
            </Paper>

            {!selectedOntologyFile && (
              <Alert severity="info">
                Selecciona un archivo TTL de ontologia para iniciar el cruce de IDs.
              </Alert>
            )}

            {ontologyMatchState.hasCompared && ontologyMatchState.sharedIdsCount === 0 && (
              <Alert severity="warning">
                No se encontraron IDs coincidentes por UUID entre el archivo base y la ontologia.
              </Alert>
            )}

            {ontologyMatchState.hasCompared && ontologyMatchState.sharedIdsCount > 0 && (
              <Stack spacing={1.1}>
                {ontologyMatchState.shared.map((matchItem) => (
                  <Paper key={matchItem.id} variant="outlined" sx={{ p: 1.2, borderRadius: 1.25 }}>
                    <Stack spacing={0.85}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {matchItem.matchLabel}: {matchItem.id}
                      </Typography>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Formas del ID en archivo base (prioridad: sujeto principal)
                        </Typography>
                        <Stack spacing={0.35}>
                          {matchItem.sourceForms.map((sourceValue) => (
                            <Typography key={`${matchItem.id}-src-${sourceValue}`} variant="caption" sx={{ fontFamily: 'monospace' }}>
                              - {sourceValue}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Contexto del ID en archivo base (prioridad: sujeto principal, incluye type)
                        </Typography>
                        {matchItem.sourceContexts.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Sin bloque de contexto adicional para este ID en el archivo base.
                          </Typography>
                        ) : (
                          <Stack spacing={0.6}>
                            {matchItem.sourceContexts.map((ctx, ctxIndex) => (
                              <Paper key={`${matchItem.id}-src-ctx-${ctx.subjectId}-${ctxIndex}`} variant="outlined" sx={{ p: 0.8, borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                                  {ctx.prefLabel || ctx.subjectId}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                  {ctx.subjectId}
                                </Typography>
                                {ctx.notation && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>notation:</strong> {ctx.notation}
                                  </Typography>
                                )}
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  <strong>type:</strong> {ctx.rdfTypes.length > 0 ? ctx.rdfTypes.join(', ') : 'Sin rdf:type detectado'}
                                </Typography>
                                {ctx.matchedTokens.length > 0 && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>Tokens coincidentes:</strong> {ctx.matchedTokens.join(' | ')}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                  {ctx.blockPreview}
                                </Typography>
                              </Paper>
                            ))}
                          </Stack>
                        )}
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Formas del ID en ontologia (prioridad: sujeto principal)
                        </Typography>
                        <Stack spacing={0.35}>
                          {matchItem.ontologyForms.map((ontologyValue) => (
                            <Typography key={`${matchItem.id}-onto-${ontologyValue}`} variant="caption" sx={{ fontFamily: 'monospace' }}>
                              - {ontologyValue}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Contexto del ID en ontologia (prioridad: sujeto principal, incluye type)
                        </Typography>
                        {matchItem.ontologyContexts.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Sin bloque de contexto adicional para este ID en la ontologia.
                          </Typography>
                        ) : (
                          <Stack spacing={0.6}>
                            {matchItem.ontologyContexts.map((ctx, ctxIndex) => (
                              <Paper key={`${matchItem.id}-onto-ctx-${ctx.subjectId}-${ctxIndex}`} variant="outlined" sx={{ p: 0.8, borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                                  {ctx.prefLabel || ctx.subjectId}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                  {ctx.subjectId}
                                </Typography>
                                {ctx.notation && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>notation:</strong> {ctx.notation}
                                  </Typography>
                                )}
                                {ctx.date && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>date:</strong> {ctx.date}
                                  </Typography>
                                )}
                                {ctx.versionInfo && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>versionInfo:</strong> {ctx.versionInfo}
                                  </Typography>
                                )}
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  <strong>type:</strong> {ctx.rdfTypes.length > 0 ? ctx.rdfTypes.join(', ') : 'Sin rdf:type detectado'}
                                </Typography>
                                {ctx.matchedTokens.length > 0 && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>Tokens coincidentes:</strong> {ctx.matchedTokens.join(' | ')}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                  {ctx.blockPreview}
                                </Typography>
                              </Paper>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                ))}

                {ontologyMatchState.sharedOverflow > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    +{ontologyMatchState.sharedOverflow} coincidencias adicionales no mostradas.
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {viewMode === 'ontologyMissing' && isPrepared && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                IDs principales del TTL base que no estan en ninguna ontologia
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Carga uno o varios TTL de ontologia y encuentra los IDs del archivo principal que no aparecen en ninguna ontologia cargada. Solo se consideran IDs definidos como sujeto principal en el TTL base. La comparacion se hace por UUID normalizado.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  component="label"
                >
                  Añadir ontologias TTL
                  <input
                    hidden
                    multiple
                    type="file"
                    accept=".ttl,text/turtle"
                    onChange={handleOntologyMissingFilesSelect}
                  />
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={selectedOntologyMissingFiles.length === 0}
                  onClick={handleClearOntologyMissingFiles}
                >
                  Limpiar ontologias
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  disabled={!sourceTtlText || selectedOntologyMissingFiles.length === 0 || isOntologyMissingProcessing}
                  onClick={handleRunOntologyMissingCheck}
                >
                  {isOntologyMissingProcessing ? 'Buscando IDs faltantes...' : 'Buscar IDs faltantes'}
                </Button>
              </Stack>
            </Paper>

            {selectedOntologyMissingFiles.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Ontologias cargadas ({selectedOntologyMissingFiles.length})
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  {selectedOntologyMissingFiles.map((file) => {
                    const fileKey = `${file.name}::${file.size}::${file.lastModified}`;
                    return (
                      <Chip
                        key={fileKey}
                        size="small"
                        label={`${file.name} (${formatFileSize(file.size)})`}
                        onDelete={() => handleRemoveOntologyMissingFile(file)}
                      />
                    );
                  })}
                </Stack>
              </Paper>
            )}

            {selectedOntologyMissingFiles.length === 0 && (
              <Alert severity="info">
                Añade uno o varios TTL de ontologia para ejecutar la comparacion.
              </Alert>
            )}

            {ontologyMissingState.hasCompared && ontologyMissingState.missingIdsCount === 0 && (
              <Alert severity="success">
                Todos los IDs principales del archivo base aparecen en al menos una ontologia cargada.
              </Alert>
            )}

            {ontologyMissingState.hasCompared && ontologyMissingState.missingIdsCount > 0 && (
              <Stack spacing={1.1}>
                {ontologyMissingState.missing.map((missingItem) => (
                  <Paper key={missingItem.id} variant="outlined" sx={{ p: 1.2, borderRadius: 1.25 }}>
                    <Stack spacing={0.8}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        ID faltante: {missingItem.id}
                      </Typography>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Formas del sujeto principal en el TTL base
                        </Typography>
                        <Stack spacing={0.35}>
                          {missingItem.sourceForms.map((sourceValue) => (
                            <Typography key={`${missingItem.id}-src-${sourceValue}`} variant="caption" sx={{ fontFamily: 'monospace' }}>
                              - {sourceValue}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Contexto del sujeto principal en TTL base (incluye type)
                        </Typography>
                        {missingItem.sourceContexts.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Sin bloque de contexto disponible para este ID.
                          </Typography>
                        ) : (
                          <Stack spacing={0.6}>
                            {missingItem.sourceContexts.map((ctx, ctxIndex) => (
                              <Paper key={`${missingItem.id}-ctx-${ctx.subjectId}-${ctxIndex}`} variant="outlined" sx={{ p: 0.8, borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                                  {ctx.prefLabel || ctx.subjectId}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                  {ctx.subjectId}
                                </Typography>
                                {ctx.notation && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>notation:</strong> {ctx.notation}
                                  </Typography>
                                )}
                                <Typography variant="caption" sx={{ display: 'block' }}>
                                  <strong>type:</strong> {ctx.rdfTypes.length > 0 ? ctx.rdfTypes.join(', ') : 'Sin type detectado'}
                                </Typography>
                                {ctx.matchedTokens.length > 0 && (
                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                    <strong>Tokens coincidentes:</strong> {ctx.matchedTokens.join(' | ')}
                                  </Typography>
                                )}
                              </Paper>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                ))}

                {ontologyMissingState.missingOverflow > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    +{ontologyMissingState.missingOverflow} IDs faltantes adicionales no mostrados.
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {viewMode === 'objects' && isPrepared && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Vista de objetos (RealObject / SpatialRegion)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Estructura jerarquica: raiz -&gt; clase -&gt; objeto. Cada objeto incluye codigo (skos:notation), nombre (skos:prefLabel), tipos, y atributos tennet con su cabecera resuelta.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={handleExpandAllObjects}>
                  Expandir todo
                </Button>
                <Button size="small" variant="outlined" onClick={handleCollapseAllObjects}>
                  Colapsar todo
                </Button>
              </Stack>
            </Paper>

            {objectsData.length === 0 && (
              <Alert severity="info">
                No se detectaron objetos con tipo raiz nen2660:RealObject o nen2660:SpatialRegion en el archivo cargado.
              </Alert>
            )}

            <Stack spacing={1.25}>
              {objectTreeData.map((rootNode) => {
                const isExpandedRoot = !!expandedObjectRoots[rootNode.id];

                return (
                  <Paper key={rootNode.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => handleObjectRootToggle(rootNode.id)}
                      sx={{ justifyContent: 'space-between', textTransform: 'none', color: 'inherit' }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        {isExpandedRoot ? <ExpandMore /> : <ChevronRight />}
                        <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {rootNode.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} noWrap>
                            {rootNode.id}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end' }}>
                        <Chip size="small" variant="outlined" label={`Clases: ${rootNode.classes.length}`} />
                        <Chip size="small" label={`Objetos: ${rootNode.objectsCount}`} />
                      </Stack>
                    </Button>

                    <Collapse in={isExpandedRoot} timeout="auto" unmountOnExit>
                      <Divider sx={{ my: 1.1 }} />
                      {rootNode.classes.length === 0 ? (
                        <Alert severity="info">No se han detectado clases con objetos para esta raiz.</Alert>
                      ) : (
                        <Stack spacing={1}>
                          {rootNode.classes.map((classNode) => {
                            const classKey = getObjectClassKey(rootNode.id, classNode.id);
                            const isExpandedClass = !!expandedObjectClasses[classKey];

                            return (
                              <Paper key={classKey} variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                                <Button
                                  fullWidth
                                  variant="text"
                                  onClick={() => handleObjectClassToggle(rootNode.id, classNode.id)}
                                  sx={{ justifyContent: 'space-between', textTransform: 'none', color: 'inherit' }}
                                >
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                    {isExpandedClass ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                                    <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                        Clase: {classNode.label}
                                      </Typography>
                                      {classNode.id !== '__SIN_CLASE__' && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} noWrap>
                                          {classNode.id}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Stack>
                                  <Chip size="small" label={`${classNode.objects.length} objetos`} />
                                </Button>

                                <Collapse in={isExpandedClass} timeout="auto" unmountOnExit>
                                  <Divider sx={{ my: 1 }} />
                                  <Stack spacing={1}>
                                    {classNode.objects.map((objectItem) => {
                                      const isExpandedObject = !!expandedObjectCards[objectItem.id];

                                      return (
                                        <Paper key={objectItem.id} variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                                          <Button
                                            fullWidth
                                            variant="text"
                                            onClick={() => handleObjectCardToggle(objectItem.id)}
                                            sx={{ justifyContent: 'space-between', textTransform: 'none', color: 'inherit' }}
                                          >
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                              {isExpandedObject ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                                              <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                                  {objectItem.notation ? `${objectItem.notation} - ${objectItem.label}` : objectItem.label}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} noWrap>
                                                  {objectItem.id}
                                                </Typography>
                                              </Box>
                                            </Stack>
                                            <Chip size="small" label={`Atributos: ${objectItem.attributes.length}`} />
                                          </Button>

                                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              onClick={() => handleViewObjectGraph(objectItem.id)}
                                            >
                                              Ver grafo
                                            </Button>
                                          </Box>

                                          <Collapse in={isExpandedObject} timeout="auto" unmountOnExit>
                                            <Divider sx={{ my: 1 }} />
                                            <Stack spacing={1.1}>
                                              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                                                <Typography variant="body2"><strong>Codigo:</strong> {objectItem.notation || '-'}</Typography>
                                                <Typography variant="body2"><strong>Nombre:</strong> {objectItem.label}</Typography>
                                                <Typography variant="body2"><strong>ID:</strong> {objectItem.id}</Typography>
                                                <Typography variant="body2">
                                                  <strong>Tipo raiz:</strong> {objectItem.rootTypes.length > 0 ? objectItem.rootTypes.map((item) => item.label).join(', ') : '-'}
                                                </Typography>
                                                <Typography variant="body2">
                                                  <strong>Clase(s):</strong> {objectItem.classTypes.length > 0 ? objectItem.classTypes.map((item) => item.label).join(' | ') : '-'}
                                                </Typography>
                                                {objectItem.classParent && (
                                                  <Typography variant="body2">
                                                    <strong>Clase padre:</strong> {objectItem.classParent.label}
                                                  </Typography>
                                                )}
                                                {objectItem.classChild && (
                                                  <Typography variant="body2">
                                                    <strong>Subclase:</strong> {objectItem.classChild.label}
                                                  </Typography>
                                                )}
                                              </Paper>

                                              <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1.25 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.6 }}>
                                                  Atributos tennet ({objectItem.attributes.length})
                                                </Typography>

                                                {objectItem.attributes.length === 0 ? (
                                                  <Typography variant="caption" color="text.secondary">Sin atributos tennet detectados.</Typography>
                                                ) : (
                                                  <Stack spacing={0.9}>
                                                    {objectItem.attributes.map((attribute) => (
                                                      <Box key={`${objectItem.id}-${attribute.id}`} sx={{ pl: 1, borderLeft: '3px solid #cbd5e1' }}>
                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                          <strong>{attribute.label}</strong>
                                                          {attribute.typeLabel ? ` (${attribute.typeLabel})` : ''}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', mb: 0.25 }}>
                                                          {attribute.id}
                                                        </Typography>
                                                        {attribute.values.map((value, index) => (
                                                          <Typography key={`${attribute.id}-${index}`} variant="caption" sx={{ display: 'block' }}>
                                                            - {value.label}
                                                            {!value.isLiteral && value.id && (
                                                              <Typography component="span" color="text.secondary" sx={{ ml: 0.75, fontFamily: 'monospace' }}>
                                                                ({value.id})
                                                              </Typography>
                                                            )}
                                                          </Typography>
                                                        ))}
                                                      </Box>
                                                    ))}
                                                  </Stack>
                                                )}
                                              </Paper>
                                            </Stack>
                                          </Collapse>
                                        </Paper>
                                      );
                                    })}
                                  </Stack>
                                </Collapse>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}
                    </Collapse>
                  </Paper>
                );
              })}
            </Stack>

            <Dialog open={isObjectGraphDialogOpen} onClose={handleCloseObjectGraphDialog} fullScreen>
              <DialogTitle sx={{ fontWeight: 700 }}>Grafo de relaciones</DialogTitle>
              <DialogContent dividers sx={{ p: 1.5 }}>
                <Stack spacing={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    {generalObjectFocusMode
                      ? 'Misma visualizacion de Vista General, filtrada al objeto: Raiz -> Clase, Objeto -> Clase, Clase -> VerificationPlan y VerificationPlan -> Requirement.'
                      : 'Misma visualizacion de Vista General completa: Raiz -> Clase -> Objeto/Region -> VerificationPlan -> Requirement.'}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {selectedGeneralNode && (
                      <Chip size="small" color="primary" label={`Objeto: ${selectedGeneralNode.label}`} />
                    )}
                    {generalObjectFocusMode && (
                      <Chip size="small" color="secondary" label="Filtro: solo relaciones del objeto" />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Chip size="small" label="N1: Raiz" sx={{ bgcolor: '#dbeafe' }} />
                    <Chip size="small" label="N2: Clase" sx={{ bgcolor: '#dcfce7' }} />
                    <Chip size="small" label="N2: Objeto" sx={{ bgcolor: '#e0f2fe' }} />
                    <Chip size="small" label="N3: VerificationPlans" sx={{ bgcolor: '#ede9fe' }} />
                    <Chip size="small" label="N4: Requirements" sx={{ bgcolor: '#fee2e2' }} />
                  </Stack>

                  <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.25} alignItems="stretch">
                    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, flex: 1, minWidth: 0 }}>
                      {renderGeneralGraphCanvas(true, { viewportHeight: 'calc(100vh - 295px)' })}
                    </Paper>
                    {selectedGeneralNode && (
                      <Box sx={{ width: { xs: '100%', xl: 390 }, flexShrink: 0 }}>
                        {renderSelectedGeneralNodeInfoPanel({
                          title: 'Detalles del nodo',
                          maxHeight: 'calc(100vh - 295px)'
                        })}
                      </Box>
                    )}
                  </Stack>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button size="small" variant="contained" onClick={handleCloseObjectGraphDialog}>
                      Cerrar
                    </Button>
                  </Box>
                </Stack>
              </DialogContent>
            </Dialog>
          </Stack>
        )}

        {viewMode === 'general' && isPrepared && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Vista general de relaciones
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                {generalObjectFocusMode
                  ? 'Flujo en foco de objeto: N1 raices - N2 objeto y clase - N3 verificationPlans - N4 requirements. Conexiones: Raiz->Clase, Objeto->Clase, Clase->VerificationPlan y VerificationPlan->Requirement.'
                  : 'Flujo de arriba hacia abajo: raices -&gt; clases -&gt; objetos/regiones -&gt; verificationPlans -&gt; requirements. Haz clic en un nodo para enfocarlo.'}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined" onClick={handleClearGeneralSelection} disabled={!selectedGeneralNodeId}>
                  Limpiar seleccion
                </Button>
                {generalObjectFocusMode && (
                  <Button size="small" variant="outlined" onClick={handleDisableObjectGraphFocus}>
                    Ver grafo completo
                  </Button>
                )}
                {selectedGeneralNode && (
                  <Chip size="small" color="primary" label={`Seleccionado: ${selectedGeneralNode.label}`} />
                )}
                {generalObjectFocusMode && selectedGeneralNode && (
                  <Chip size="small" color="secondary" label="Filtro: solo relaciones del objeto" />
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {generalObjectFocusMode ? (
                  <>
                    <Chip size="small" label="N1: Raiz" sx={{ bgcolor: '#dbeafe' }} />
                    <Chip size="small" label="N2: Clase" sx={{ bgcolor: '#dcfce7' }} />
                    <Chip size="small" label="N2: Objeto" sx={{ bgcolor: '#e0f2fe' }} />
                    <Chip size="small" label="N3: VerificationPlans" sx={{ bgcolor: '#ede9fe' }} />
                    <Chip size="small" label="N4: Requirements" sx={{ bgcolor: '#fee2e2' }} />
                  </>
                ) : (
                  <>
                    <Chip size="small" label="Raiz -> Clase" sx={{ bgcolor: '#dbeafe' }} />
                    <Chip size="small" label="Clase -> Objeto/Region" sx={{ bgcolor: '#dcfce7' }} />
                    <Chip size="small" label="Objeto/Region -> VerificationPlan" sx={{ bgcolor: '#fef3c7' }} />
                    <Chip size="small" label="VerificationPlan -> Requirement" sx={{ bgcolor: '#ede9fe' }} />
                    <Chip size="small" label="Clase -> Requirement" sx={{ bgcolor: '#fee2e2' }} />
                  </>
                )}
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.25} alignItems="stretch">
              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, flex: 1, minWidth: 0 }}>
                {renderGeneralGraphCanvas(true, { viewportHeight: '72vh' })}
              </Paper>
              {selectedGeneralNode && (
                <Box sx={{ width: { xs: '100%', xl: 390 }, flexShrink: 0 }}>
                  {renderSelectedGeneralNodeInfoPanel({
                    title: 'Detalles del nodo',
                    maxHeight: '72vh'
                  })}
                </Box>
              )}
            </Stack>
          </Stack>
        )}

        {viewMode === 'classes' && classesData.length > 0 && (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Arbol de clases (RealObject / SpatialRegion)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Se incluyen solo recursos que tengan ambos tipos: owl:Class y sh:NodeShape. Las clases se agrupan solo por rdfs:subClassOf a nen2660:RealObject o nen2660:SpatialRegion.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AccountTree fontSize="small" />}
                  onClick={handleOpenClassInheritanceGraphDialog}
                  disabled={classInheritanceGraphData.summary.classes === 0}
                >
                  Ver grafo
                </Button>
                <Button size="small" variant="outlined" onClick={handleExpandAllClassDetails}>
                  Expandir detalle de clases
                </Button>
                <Button size="small" variant="outlined" onClick={handleCollapseAllClassDetails}>
                  Colapsar detalle de clases
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {classTreeData.map((rootNode) => {
                  const isExpandedRoot = !!expandedClassRoots[rootNode.id];

                  return (
                    <Paper key={rootNode.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.25 }}>
                      <Button
                        fullWidth
                        variant="text"
                        onClick={() => handleClassRootToggle(rootNode.id)}
                        sx={{ justifyContent: 'space-between', textTransform: 'none' }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          {isExpandedRoot ? <ExpandMore /> : <ChevronRight />}
                          <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                              {rootNode.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace' }}>
                              {rootNode.id}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip size="small" label={`${rootNode.classes.length} clases`} />
                      </Button>

                      <Collapse in={isExpandedRoot} timeout="auto" unmountOnExit>
                        <Divider sx={{ my: 1.25 }} />
                        {rootNode.classes.length === 0 ? (
                          <Alert severity="info">No se encontraron clases para esta raiz.</Alert>
                        ) : (
                          <Stack spacing={1}>
                            {rootNode.classes.map((cls) => {
                              const isExpandedClass = !!expandedClassCards[cls.id];

                              return (
                                <Paper key={cls.id} variant="outlined" sx={{ p: 1.25, borderRadius: 1.25 }}>
                                  <Button
                                    fullWidth
                                    variant="text"
                                    onClick={() => handleClassCardToggle(cls.id)}
                                    sx={{ justifyContent: 'space-between', textTransform: 'none' }}
                                  >
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                      {isExpandedClass ? <ExpandMore /> : <ChevronRight />}
                                      <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                          {cls.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace' }}>
                                          {cls.id}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                    <Stack direction="row" spacing={0.75}>
                                      <Chip size="small" label={`Reqs: ${cls.requirements.length}`} />
                                      <Chip size="small" label={`Props: ${cls.properties.length}`} />
                                    </Stack>
                                  </Button>

                                  <Collapse in={isExpandedClass} timeout="auto" unmountOnExit>
                                    <Divider sx={{ my: 1 }} />
                                    <Stack spacing={1.1}>
                                      <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1 }}>
                                        <Typography variant="body2"><strong>Nombre:</strong> {cls.label}</Typography>
                                        <Typography variant="body2"><strong>ID:</strong> {cls.id}</Typography>
                                        <Typography variant="body2">
                                          <strong>{cls.classCodePropertyLabel}:</strong> {cls.classCode || '-'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                          {cls.classCodePropertyId}
                                        </Typography>
                                      </Paper>

                                      <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                          Requirements ({cls.requirements.length})
                                        </Typography>
                                        {cls.requirements.length === 0 ? (
                                          <Typography variant="caption" color="text.secondary">Sin hasRequirement.</Typography>
                                        ) : (
                                          <Stack spacing={0.6}>
                                            {cls.requirements.map((req) => (
                                              <Typography key={`${cls.id}-${req.id}`} variant="caption" sx={{ display: 'block' }}>
                                                - {req.label}
                                                <Typography component="span" color="text.secondary" sx={{ ml: 0.75, fontFamily: 'monospace' }}>
                                                  ({req.id})
                                                </Typography>
                                              </Typography>
                                            ))}
                                          </Stack>
                                        )}
                                      </Paper>

                                      <Paper variant="outlined" sx={{ p: 1.1, borderRadius: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                          Propiedades SH ({cls.properties.length})
                                        </Typography>
                                        {cls.properties.length === 0 ? (
                                          <Typography variant="caption" color="text.secondary">Sin bloques sh:property.</Typography>
                                        ) : (
                                          <Stack spacing={0.8}>
                                            {cls.properties.map((prop, index) => (
                                              <Box key={`${cls.id}-${prop.pathId}-${index}`} sx={{ pl: 1, borderLeft: '3px solid #cbd5e1' }}>
                                                <Typography variant="caption" sx={{ display: 'block' }}>
                                                  <strong>Path:</strong> {prop.pathLabel}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                                                  {prop.pathId}
                                                </Typography>
                                                {prop.minCount !== null && (
                                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                                    <strong>minCount:</strong> {prop.minCount}
                                                  </Typography>
                                                )}
                                                {prop.hasValue && (
                                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                                    <strong>hasValue:</strong> {prop.hasValue}
                                                  </Typography>
                                                )}
                                                {prop.classId && (
                                                  <Typography variant="caption" sx={{ display: 'block' }}>
                                                    <strong>class:</strong> {prop.classLabel}
                                                    <Typography component="span" color="text.secondary" sx={{ ml: 0.75, fontFamily: 'monospace' }}>
                                                      ({prop.classId})
                                                    </Typography>
                                                  </Typography>
                                                )}
                                              </Box>
                                            ))}
                                          </Stack>
                                        )}
                                      </Paper>
                                    </Stack>
                                  </Collapse>
                                </Paper>
                              );
                            })}
                          </Stack>
                        )}
                      </Collapse>
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>
          </Stack>
        )}

        <Dialog open={isClassInheritanceGraphDialogOpen} onClose={handleCloseClassInheritanceGraphDialog} fullScreen>
          <DialogTitle sx={{ fontWeight: 700 }}>Grafo de herencia de clases</DialogTitle>
          <DialogContent dividers sx={{ p: 1.5 }}>
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Arbol completo de herencia de clases detectado en el TTL: se incluyen clases, raices conocidas y padres externos presentes en rdfs:subClassOf.
              </Typography>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" color="primary" label={`Clases: ${classInheritanceGraphData.summary.classes}`} />
                <Chip size="small" variant="outlined" label={`Nodos: ${classInheritanceGraphData.summary.nodes}`} />
                <Chip size="small" variant="outlined" label={`Relaciones: ${classInheritanceGraphData.summary.edges}`} />
                <Chip size="small" variant="outlined" label={`Niveles: ${classInheritanceGraphData.summary.levels}`} />
                <Chip size="small" variant="outlined" label={`Padres externos: ${classInheritanceGraphData.summary.externalParents}`} />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" label="Raiz" sx={{ bgcolor: '#dbeafe' }} />
                <Chip size="small" label="Clase" sx={{ bgcolor: '#dcfce7' }} />
                <Chip size="small" label="Padre externo" sx={{ bgcolor: '#fef3c7' }} />
                <Chip size="small" label="Sin subClassOf" sx={{ bgcolor: '#fee2e2' }} />
              </Stack>

              <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                {renderClassInheritanceGraphCanvas({ viewportHeight: 'calc(100vh - 292px)' })}
              </Paper>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" variant="contained" onClick={handleCloseClassInheritanceGraphDialog}>
                  Cerrar
                </Button>
              </Box>
            </Stack>
          </DialogContent>
        </Dialog>
      </Stack>
    </Paper>
  );
}

export default SemanticExplorerTab;